import { z } from 'zod';
import createFetchClient from 'openapi-fetch';

// src/ask.ts
var PAGE_LIMIT_DEFAULT = 20;
var PAGE_LIMIT_MAX = 100;
var MAX_SORT_FIELDS = 3;
var RESERVED_QUERY_KEYS = ["limit", "cursor", "sort"];
var TIEBREAKER = "id";
var PaginationError = class extends Error {
  name = "PaginationError";
};
var BASE64URL = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
function asciiJson(value) {
  return JSON.stringify(value).replace(
    /[\u007f-\uffff]/g,
    (char) => `\\u${char.charCodeAt(0).toString(16).padStart(4, "0")}`
  );
}
function toBase64Url(ascii) {
  let out = "";
  for (let i = 0; i < ascii.length; i += 3) {
    const a = ascii.charCodeAt(i);
    const b = i + 1 < ascii.length ? ascii.charCodeAt(i + 1) : void 0;
    const c = i + 2 < ascii.length ? ascii.charCodeAt(i + 2) : void 0;
    out += BASE64URL[a >> 2] ?? "";
    out += BASE64URL[(a & 3) << 4 | (b === void 0 ? 0 : b >> 4)] ?? "";
    if (b !== void 0) out += BASE64URL[(b & 15) << 2 | (c === void 0 ? 0 : c >> 6)] ?? "";
    if (c !== void 0) out += BASE64URL[c & 63] ?? "";
  }
  return out;
}
function fromBase64Url(encoded) {
  let out = "";
  let buffer = 0;
  let bits = 0;
  for (const char of encoded) {
    const index = BASE64URL.indexOf(char);
    if (index < 0) return void 0;
    buffer = buffer << 6 | index;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out += String.fromCharCode(buffer >> bits & 255);
    }
  }
  return out;
}
var CursorPayloadSchema = z.strictObject({
  v: z.array(z.union([z.string(), z.number(), z.boolean()])),
  id: z.string().min(1)
});
function encodeCursor(position) {
  position.v.forEach((value, index) => {
    if (value === null || value === void 0) {
      throw new PaginationError(
        `Cursor sort value at index ${String(index)} is ${String(value)}; sortable fields must be non-nullable.`
      );
    }
  });
  if (position.id === "") {
    throw new PaginationError("Cursor id is empty; the tiebreaker must identify a row.");
  }
  return toBase64Url(asciiJson({ v: [...position.v], id: position.id }));
}
function decodeCursor(raw) {
  const ascii = fromBase64Url(raw);
  if (ascii === void 0) return { ok: false, reason: "not base64url" };
  let parsed;
  try {
    parsed = JSON.parse(ascii);
  } catch {
    return { ok: false, reason: "not JSON" };
  }
  const payload = CursorPayloadSchema.safeParse(parsed);
  if (!payload.success) return { ok: false, reason: "not a cursor position" };
  return {
    ok: true,
    position: Object.freeze({ v: Object.freeze(payload.data.v), id: payload.data.id })
  };
}
function trySort(raw, sortable, defaultSort) {
  const source = raw === void 0 || raw.trim() === "" ? defaultSort : raw;
  const segments = source.split(",");
  if (segments.length > MAX_SORT_FIELDS) {
    return {
      ok: false,
      reason: `at most ${String(MAX_SORT_FIELDS)} sort fields, got ${String(segments.length)}`
    };
  }
  const fields = [];
  const seen = /* @__PURE__ */ new Set();
  for (const segment of segments) {
    const trimmed = segment.trim();
    const direction = trimmed.startsWith("-") ? "desc" : "asc";
    const field = direction === "desc" ? trimmed.slice(1) : trimmed;
    if (field === "") return { ok: false, reason: "a sort segment named no field" };
    if (!sortable.includes(field)) {
      return {
        ok: false,
        reason: `"${field}" is not sortable here; allowed: ${sortable.join(", ")}`
      };
    }
    if (seen.has(field)) return { ok: false, reason: `"${field}" appears twice` };
    seen.add(field);
    fields.push({ field, direction });
  }
  if (!seen.has(TIEBREAKER)) fields.push({ field: TIEBREAKER, direction: "asc" });
  return { ok: true, sort: Object.freeze(fields) };
}
function parseSort(raw, sortable, defaultSort) {
  const attempt = trySort(raw, sortable, defaultSort);
  if (!attempt.ok) throw new PaginationError(`Invalid sort: ${attempt.reason}`);
  return attempt.sort;
}
function assertSortable(sortable, defaultSort) {
  if (sortable.length === 0) {
    throw new PaginationError("sortable is empty: a list with no defined order cannot be paged.");
  }
  if (new Set(sortable).size !== sortable.length) {
    throw new PaginationError(`sortable names a field twice: ${sortable.join(", ")}`);
  }
  const attempt = trySort(defaultSort, sortable, defaultSort);
  if (!attempt.ok) {
    throw new PaginationError(`Invalid defaultSort "${defaultSort}": ${attempt.reason}`);
  }
}
var limitSchema = z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(PAGE_LIMIT_DEFAULT);
function limitFor(limitMax) {
  if (limitMax === void 0) return limitSchema;
  if (!Number.isInteger(limitMax) || limitMax < PAGE_LIMIT_DEFAULT || limitMax > PAGE_LIMIT_MAX) {
    throw new PaginationError(
      `limitMax ${String(limitMax)} must be a whole number from the default limit (${String(PAGE_LIMIT_DEFAULT)}) to PAGE_LIMIT_MAX (${String(PAGE_LIMIT_MAX)}).`
    );
  }
  return z.coerce.number().int().min(1).max(limitMax).default(PAGE_LIMIT_DEFAULT);
}
function paginationShape(config) {
  assertSortable(config.sortable, config.defaultSort);
  return {
    limit: limitFor(config.limitMax),
    // The output is the decoded position, not the string: a handler that has to decode a cursor
    // itself has to handle a failure the boundary has already ruled out. `ctx.addIssue` rather
    // than a throw, so the failure arrives as a zod issue on the `cursor` path.
    cursor: z.string().transform((raw, ctx) => {
      const decoded = decodeCursor(raw);
      if (!decoded.ok) {
        ctx.addIssue({ code: "custom", message: `Invalid cursor: ${decoded.reason}` });
        return z.NEVER;
      }
      return decoded.position;
    }).optional(),
    // Parsed once, here. A handler receives `[{ field, direction }, …]`, never a string it has to
    // parse again — which is how two endpoints end up disagreeing about the same syntax.
    sort: z.string().optional().transform((raw, ctx) => {
      const attempt = trySort(raw, config.sortable, config.defaultSort);
      if (!attempt.ok) {
        ctx.addIssue({ code: "custom", message: `Invalid sort: ${attempt.reason}` });
        return z.NEVER;
      }
      return attempt.sort;
    })
  };
}
function paginationQuery(config) {
  return z.strictObject(paginationShape(config));
}
function listQuery(config) {
  const filters = config.filters ?? {};
  for (const key of Object.keys(filters)) {
    if (RESERVED_QUERY_KEYS.includes(key)) {
      throw new PaginationError(
        `Filter "${key}" shadows a reserved paging parameter (${RESERVED_QUERY_KEYS.join(", ")}).`
      );
    }
  }
  return z.strictObject({ ...paginationShape(config), ...filters });
}
var PageInfoSchema = z.strictObject({
  nextCursor: z.string().nullable(),
  hasMore: z.boolean()
});
function pageEnvelope(item) {
  return z.strictObject({ items: z.array(item), page: PageInfoSchema });
}
function freezePage(items, nextCursor, hasMore) {
  return Object.freeze({
    items: Object.freeze(items),
    page: Object.freeze({ nextCursor, hasMore })
  });
}
function emptyPage() {
  return freezePage([], null, false);
}
function fetchLimit(limit) {
  return limit + 1;
}
function positionOf(row, sort) {
  const values = sort.slice(0, -1).map((field) => {
    const value = row[field.field];
    if (value === void 0 || value === null) {
      throw new PaginationError(
        `Row carries no value for the sort field "${field.field}"; a cursor cannot be built from it.`
      );
    }
    if (value instanceof Date) return value.toISOString();
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      return value;
    }
    throw new PaginationError(
      `Sort field "${field.field}" holds a ${typeof value}, which cannot go in a cursor.`
    );
  });
  return { v: values, id: row.id };
}
function pageOf(rows, request) {
  const { limit, sort } = request;
  if (!Number.isInteger(limit) || limit < 1) {
    throw new PaginationError(`Page limit must be a positive integer, got ${String(limit)}.`);
  }
  const last = sort[sort.length - 1];
  if (last === void 0 || last.field !== TIEBREAKER) {
    throw new PaginationError(
      `A sort spec must end with the "${TIEBREAKER}" tiebreaker, or paging is not over a total order.`
    );
  }
  const items = rows.slice(0, limit);
  const hasMore = rows.length > limit;
  const tail = hasMore ? items[items.length - 1] : void 0;
  return freezePage(
    items,
    tail === void 0 ? null : encodeCursor(positionOf(tail, sort)),
    hasMore
  );
}
function keysetPredicate(sort, position) {
  const values = [...position.v, position.id];
  if (sort.length !== values.length) {
    throw new PaginationError(
      `Cursor holds ${String(position.v.length)} sort value(s) plus an id, but the sort names ${String(sort.length)} field(s). The cursor was issued for a different sort.`
    );
  }
  const or = sort.map((field, index) => ({
    and: Object.freeze(
      sort.slice(0, index + 1).map((prefix, i) => {
        const value = values[i];
        if (value === void 0) {
          throw new PaginationError(`Cursor has no value for sort field "${prefix.field}".`);
        }
        return {
          field: prefix.field,
          op: i < index ? "eq" : prefix.direction === "desc" ? "lt" : "gt",
          value
        };
      })
    )
  }));
  return Object.freeze({ or: Object.freeze(or) });
}

// src/search.ts
var CATEGORY_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
var SearchUrgencySchema = z.enum(["urgente", "hoy", "semana", "flexible"]);
var SearchModeSchema = z.enum(["quote", "booking"]);
var SEARCH_SORTABLE = ["distanceMetres"];
var SEARCH_DEFAULT_SORT = "distanceMetres";
var SearchQuerySchema = listQuery({
  sortable: SEARCH_SORTABLE,
  defaultSort: SEARCH_DEFAULT_SORT,
  filters: {
    what: z.string().regex(CATEGORY_SLUG).optional(),
    // Free text until `GET /places/suggest` exists (`W3-T06`). Required, because a radius search
    // with no centre is not a search — it is `SELECT * FROM provider_profile`.
    where: z.string().min(1).max(120),
    when: SearchUrgencySchema.optional(),
    mode: SearchModeSchema.optional()
  }
});
var POINT_DECIMALS = 3;
function isCoarse(value) {
  return Number(value.toFixed(POINT_DECIMALS)) === value;
}
var CoordinateSchema = (max2) => z.number().min(-max2).max(max2).refine(isCoarse, `must be rounded to ${String(POINT_DECIMALS)} decimal places`);
var SearchPointSchema = z.strictObject({
  latitude: CoordinateSchema(90),
  longitude: CoordinateSchema(180)
});
function coarsenPoint(point) {
  return {
    latitude: Number(point.latitude.toFixed(POINT_DECIMALS)),
    longitude: Number(point.longitude.toFixed(POINT_DECIMALS))
  };
}
var SearchCategorySchema = z.strictObject({
  slug: z.string().regex(CATEGORY_SLUG),
  name: z.string().min(1)
});
var ProviderKindSchema = z.enum(["MANITAS", "PRO"]);
var SearchResultSchema = z.strictObject({
  id: z.uuid(),
  displayName: z.string().min(1),
  kind: ProviderKindSchema,
  bio: z.string().nullable(),
  categories: z.array(SearchCategorySchema),
  // Null is "no reviews yet", which is not 0.00 (`schema.prisma:136`). A 0 here would rank a brand
  // new provider below a bad one, which is the wrong answer on a marketplace with no supply yet.
  ratingAvg: z.number().min(0).max(5).nullable(),
  ratingCount: z.number().int().nonnegative(),
  // Integer cents (`W1-T06`), never a float. Null = quote-only.
  hourlyRateCents: z.number().int().nonnegative().nullable(),
  // Metres, because `ST_DWithin` on geography takes metres.
  distanceMetres: z.number().int().nonnegative(),
  city: z.string().min(1),
  province: z.string().min(1),
  point: SearchPointSchema
});
var SearchFacetsSchema = z.strictObject({
  categories: z.array(
    z.strictObject({
      slug: z.string().regex(CATEGORY_SLUG),
      name: z.string().min(1),
      count: z.number().int().nonnegative()
    })
  ),
  kinds: z.array(
    z.strictObject({ kind: ProviderKindSchema, count: z.number().int().nonnegative() })
  )
});
var SearchResponseSchema = pageEnvelope(SearchResultSchema).extend({
  facets: SearchFacetsSchema
});

// src/provider.ts
var SERVICE_RADIUS_MAX_METRES = 2e5;
var ProviderIdSchema = z.uuid();
var ProviderProfileSchema = SearchResultSchema.omit({ distanceMetres: true }).extend({
  /**
   * How far they travel. Null is "not set", and `schema.prisma:127` says such a provider is not
   * searchable at all — so on a profile it renders as "area not stated" and never as 0 km.
   */
  serviceRadiusMetres: z.number().int().positive().max(SERVICE_RADIUS_MAX_METRES).nullable(),
  /**
   * `createdAt`. On a marketplace with no reviews yet, "here since March" is one of the few honest
   * trust signals available (`R3`) and it costs a column that already exists.
   *
   * **This is the first datetime on the wire in this package**, so it sets the convention: an
   * ISO-8601 UTC instant as a string, formatted at the display layer. It is the only option that
   * survives a JSON round trip without a reviver, and the only one that does not put a timezone in
   * the database's mouth. `state-machine.ts` uses `z.date()` because it never crosses the wire.
   */
  memberSince: z.iso.datetime()
});
var PROVIDER_CATEGORY_MAX = 20;
var ProviderAddressWriteSchema = z.strictObject({
  /** "Casa", "Taller". User copy, so no enum — and optional, because a centre needs no name. */
  label: z.string().trim().min(1).max(60).nullable(),
  line1: z.string().trim().min(1).max(200),
  line2: z.string().trim().min(1).max(200).nullable(),
  city: z.string().trim().min(1).max(100),
  province: z.string().trim().min(1).max(100),
  /** `CHECK (postal_code ~ '^[0-9]{5}$')` on the column, restated on the wire. */
  postalCode: z.string().regex(/^[0-9]{5}$/, "must be five digits"),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180)
});
var ProviderProfileWriteSchema = z.strictObject({
  displayName: z.string().trim().min(2).max(120),
  kind: ProviderKindSchema,
  bio: z.string().trim().max(2e3).nullable(),
  categories: z.array(z.string().regex(CATEGORY_SLUG)).min(1).max(PROVIDER_CATEGORY_MAX),
  serviceRadiusMetres: z.number().int().positive().max(SERVICE_RADIUS_MAX_METRES),
  hourlyRateCents: z.number().int().min(0).nullable(),
  baseAddress: ProviderAddressWriteSchema
});
var ProviderProfileOwnSchema = ProviderProfileSchema.omit({ point: true }).extend({
  baseAddress: ProviderAddressWriteSchema,
  /**
   * `W3-T08` — the trades that **invite** proof of a licence: the slugs of this provider's claimed,
   * active categories whose `requiresLicence` is true, in the order `GET /api/categories` serves
   * them (`W3-T01` §3.8). Derived from the category's column on every read and never stored, so
   * editing the category set, retiring a trade or un-gating one changes it with no backfill.
   *
   * Required, and `[]` when nothing asks: a missing key would read as "not computed", which is a
   * different statement from "no trade of yours asks".
   *
   * **On this projection only.** On a public one it would be a list of trades for which this
   * provider is not yet shown verified — an unverified-status statement, made to everyone, that
   * nothing can make true or false until `W8` receives and checks documents (spec §3.1). An invite
   * is not a gate: an unverified provider in these trades is listed, searchable and bookable.
   */
  licenceInvited: z.array(z.string().regex(CATEGORY_SLUG))
});
var ProviderAddressCreateSchema = ProviderAddressWriteSchema.omit({
  latitude: true,
  longitude: true
});
var ProviderProfileCreateSchema = ProviderProfileWriteSchema.extend({
  baseAddress: ProviderAddressCreateSchema
});
var ProviderAddressUpdateSchema = ProviderAddressCreateSchema.extend({
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional()
}).refine((address) => address.latitude === void 0 === (address.longitude === void 0), {
  message: "latitude and longitude come together or not at all",
  path: ["latitude"]
});
var ProviderProfileUpdateSchema = ProviderProfileWriteSchema.extend({
  baseAddress: ProviderAddressUpdateSchema
});
var StateMachineDefinitionError = class extends Error {
  name = "StateMachineDefinitionError";
};
var TransitionRejected = class extends Error {
  name = "TransitionRejected";
  code;
  reason;
  entity;
  from;
  event;
  constructor(entity, from, event, rejection) {
    super(`${entity}: cannot apply ${event} from ${from} \u2014 ${rejection.reason}`);
    this.entity = entity;
    this.from = from;
    this.event = event;
    this.reason = rejection.reason;
    this.code = rejection.code ?? "CONFLICT";
  }
};
var AUDIT_BASE = {
  entity: z.string().min(1),
  entityId: z.uuid(),
  action: z.string().min(1),
  fromState: z.string().min(1),
  toState: z.string().min(1),
  metadata: z.record(z.string(), z.json()).optional(),
  at: z.date()
};
var AuditRecordSchema = z.discriminatedUnion("actorType", [
  z.strictObject({ actorType: z.literal("USER"), actorId: z.uuid(), ...AUDIT_BASE }),
  z.strictObject({ actorType: z.literal("SYSTEM"), actorId: z.null(), ...AUDIT_BASE })
]);
function fail(message) {
  throw new StateMachineDefinitionError(message);
}
var froms = (from) => Array.isArray(from) ? from : [from];
function defineMachine(definition) {
  const { name, initial, states, terminal, transitions } = definition;
  if (name.trim() === "") fail("A machine needs a name: it is written into every audit record.");
  if (states.length === 0) fail(`${name}: a machine with no states is not a machine.`);
  const declared = new Set(states);
  if (!declared.has(initial)) {
    fail(`${name}: initial state "${initial}" is not in states [${states.join(", ")}].`);
  }
  for (const rule of transitions) {
    for (const from of froms(rule.from)) {
      if (!declared.has(from)) {
        fail(`${name}: rule (${from}, ${rule.on}) names unknown state "${from}".`);
      }
    }
    if (!declared.has(rule.to)) {
      fail(`${name}: rule (${String(rule.from)}, ${rule.on}) names unknown state "${rule.to}".`);
    }
  }
  const seen = /* @__PURE__ */ new Map();
  const outgoing = /* @__PURE__ */ new Map();
  for (const rule of transitions) {
    for (const from of froms(rule.from)) {
      const key = `${from} ${rule.on}`;
      const existing = seen.get(key);
      if (existing !== void 0) {
        fail(
          `${name}: two rules for (${from}, ${rule.on}) \u2014 one to "${existing}", one to "${rule.to}". Which one wins would depend on the order of the array.`
        );
      }
      seen.set(key, rule.to);
      outgoing.set(from, (outgoing.get(from) ?? 0) + 1);
    }
  }
  const terminalSet = new Set(terminal);
  for (const state of terminal) {
    if (!declared.has(state)) fail(`${name}: terminal state "${state}" is not in states.`);
  }
  for (const state of states) {
    const hasExit = (outgoing.get(state) ?? 0) > 0;
    if (hasExit && terminalSet.has(state)) {
      fail(`${name}: state "${state}" is declared terminal but has an outgoing transition.`);
    }
    if (!hasExit && !terminalSet.has(state)) {
      fail(
        `${name}: state "${state}" has no outgoing transition but is not declared terminal. Add it to \`terminal\`, or add the rule that is missing.`
      );
    }
  }
  const reachable = /* @__PURE__ */ new Set([initial]);
  const queue = [initial];
  while (queue.length > 0) {
    const current = queue.shift();
    for (const rule of transitions) {
      if (froms(rule.from).includes(current) && !reachable.has(rule.to)) {
        reachable.add(rule.to);
        queue.push(rule.to);
      }
    }
  }
  for (const state of states) {
    if (!reachable.has(state)) {
      fail(`${name}: state "${state}" is not reachable from "${initial}" by any declared rule.`);
    }
  }
  return Object.freeze({
    name,
    initial,
    states: Object.freeze([...states]),
    terminal: Object.freeze([...terminal]),
    transitions: Object.freeze(transitions.map((rule) => Object.freeze({ ...rule })))
  });
}
function ruleFor(machine, from, event) {
  return machine.transitions.find((rule) => rule.on === event && froms(rule.from).includes(from));
}
function check(machine, from, event, context) {
  const rule = ruleFor(machine, from, event);
  if (rule === void 0) {
    return {
      code: "CONFLICT",
      reason: machine.terminal.includes(from) ? `${from} is a final state: nothing further can happen to this ${machine.name}.` : `${machine.name} cannot go from ${from} via ${event}.`
    };
  }
  if (rule.guard !== void 0) {
    const verdict = rule.guard(context);
    if (verdict !== true) return { code: verdict.code ?? "CONFLICT", reason: verdict.reason };
  }
  return true;
}
function can(machine, from, event, context) {
  return check(machine, from, event, context) === true;
}
async function transition(machine, request, record) {
  const verdict = check(machine, request.from, request.event, request.context);
  if (verdict !== true) {
    throw new TransitionRejected(machine.name, request.from, request.event, verdict);
  }
  const to = ruleFor(machine, request.from, request.event).to;
  const now = request.now ?? (() => /* @__PURE__ */ new Date());
  const audit = Object.freeze({
    entity: machine.name,
    entityId: request.entityId,
    action: request.event,
    fromState: request.from,
    toState: to,
    actorType: request.actor.type,
    actorId: request.actor.type === "USER" ? request.actor.id : null,
    // Omitted rather than null when absent: Prisma's nullable Json fields do not accept a plain
    // `null`, and an absent key is what produces a NULL column.
    ...request.metadata === void 0 ? {} : { metadata: request.metadata },
    at: now()
  });
  await record(audit);
  return { to, audit };
}

// src/job.ts
var JobStatusSchema = z.enum(["DRAFT", "OPEN", "CANCELLED", "AWARDED", "IN_PROGRESS"]);
var JobEventSchema = z.enum(["PUBLISH", "CANCEL", "AWARD", "START"]);
var JobModeSchema = z.enum(["QUOTES", "AUCTION"]);
var JobUrgencySchema = SearchUrgencySchema;
function hasAnyCategory(context) {
  return context.categoryCount > 0 || {
    reason: "a job needs at least one category before it can be published \u2014 it is how providers find it",
    code: "CONFLICT"
  };
}
var jobMachine = defineMachine({
  name: "job",
  initial: "DRAFT",
  states: ["DRAFT", "OPEN", "CANCELLED", "AWARDED", "IN_PROGRESS"],
  /**
   * `OPEN` was here, and `W4-T01` promised it would leave in the same change that gave it an exit.
   * `W4-T02` was that change — the promise is kept in one edit rather than left to be noticed later,
   * because a `terminal` list that is stale reads exactly like one that is deliberate.
   *
   * `CANCELLED` is terminal for good. Reopening a cancelled job is a *new* job: a lifecycle that
   * can restart is one whose audit trail has to be read backwards before it means anything.
   *
   * `AWARDED` was here as the debt `W4-T05` declared — terminal only because its exit had no
   * producer yet — with the rule that it leaves in the same change that gives it one. **Paid by
   * `W4-T09`**: `START` is that exit, and `AWARDED` left this list in the same edit (spec §2.6). Its
   * other exit, `CANCEL` after an award, which forfeits the fee and splits it, is still money's
   * refund decision (`W5-T05`) and arrives with its own rule.
   *
   * **`IN_PROGRESS` is terminal for now, and this is the next declared debt, not an oversight.** Its
   * exit is `COMPLETED`, which nothing produces yet (`W8-T05`, `W5`); cancelling after the start is
   * not a cancellation of a call-out and is decided nowhere. An exit that always fails would be the
   * empty promise this repo refuses, and `defineMachine` refuses a non-terminal state with none. So
   * the rule is written down in advance, as it was for `AWARDED`: **`IN_PROGRESS` leaves this list in
   * the same change that gives it an exit.**
   */
  terminal: ["CANCELLED", "IN_PROGRESS"],
  transitions: [
    { from: "DRAFT", on: "PUBLISH", to: "OPEN", guard: hasAnyCategory },
    /**
     * One rule from two states, not two rules. A client cancelling a draft and a client cancelling
     * a published job are the same decision — *nobody is going to do this work* — and the refusal
     * a second attempt gets should not depend on which it was.
     *
     * Unguarded: there is nothing to check. Cancelling is always allowed while the job is the
     * client's and has not been awarded. **`AWARDED` and `IN_PROGRESS` are deliberately not in
     * `from`**: cancelling past an award is a refund decision (`W5-T05`) and arrives with its own
     * rule, and cancelling after the start is decided nowhere (`W4-T09` §2.6). Until then the route
     * answers `409 CONFLICT` — from `AWARDED` with the machine's "cannot go from" reason (it is no
     * longer final since `W4-T09`), from `IN_PROGRESS` with "final state".
     */
    { from: ["DRAFT", "OPEN"], on: "CANCEL", to: "CANCELLED" },
    /**
     * `W4-T05` §3.1. Fired only by `awardJob` (the `AwardPort` `W5-T02` declares), on the
     * transaction that moves the booking to `PAID`, with actor `SYSTEM`: what moves the job is
     * Stripe's word that the payment arrived, not a request from the client's session.
     *
     * **Unguarded, on purpose.** §3.1's guard column — *an `ACCEPTED` quote exists on the job;
     * `mode = QUOTES`* — is enforced by `awardJob`'s own precondition checks under the job lock
     * (spec §2.2 rule 4), which throw `AwardInvariantBroken`. Expressed as a guard it would need
     * quote data in `JobContext`, and every `PUBLISH` and `CANCEL` caller would then have to load a
     * quote count it never reads — or pass a value that is not true, which is the lying context the
     * comment on `JobContext` refuses.
     */
    { from: "OPEN", on: "AWARD", to: "AWARDED" },
    /**
     * `W4-T09` §2.6. Fired only by the handshake's redeem, in the transaction that marks the
     * handshake `REDEEMED` and asks money's port to start the booking, with actor `USER` — the
     * client whose session redeemed the code. What moves the job is the counterparty attesting the
     * start (ADR-013 §3), never the professional's word.
     *
     * **Unguarded, on purpose, as `AWARD` is.** The checks — the code matches and is live, the
     * caller is the booking's client and not its provider, the booking is `PAID` — are the redeem's
     * own, made under the handshake's row lock (spec §2.4), where the data is. Expressed as a guard
     * they would need handshake and booking data in `JobContext`, which every `PUBLISH` and `CANCEL`
     * caller would then have to load — or pass a value that is not true, the lying context the
     * comment on `JobContext` refuses.
     */
    { from: "AWARDED", on: "START", to: "IN_PROGRESS" }
  ]
});
var EDITABLE_IN = {
  /** A work in progress, visible to its owner and nobody else. */
  DRAFT: true,
  /**
   * Providers are reading it. Editing underneath them changes what they answered, and once
   * `W4-T03` lands it silently invalidates quotes that were priced against something else.
   * Amendment is a real feature and it needs quote invalidation, so it waits for quotes to exist
   * (operator, 2026-09-20: *"no — drafts only, expressed in the machine"*).
   */
  OPEN: false,
  /** Terminal. Editing a cancelled job is asking for a job, and that is `POST /api/jobs`. */
  CANCELLED: false,
  /**
   * `W4-T05` §2.5. An awarded job's description is what was agreed and paid against; editing it
   * would silently change the deal under a provider who has already been paid a call-out fee for it.
   */
  AWARDED: false,
  /**
   * `W4-T09` §2.6. Work has started on what was agreed and paid against; the client attested it.
   * Editing now would rewrite the job under the professional doing it.
   */
  IN_PROGRESS: false
};
function canEditJob(status) {
  return EDITABLE_IN[status] || {
    reason: `only a draft can be edited \u2014 this job is ${status}`,
    code: "CONFLICT"
  };
}
var BudgetRangeSchema = z.object({
  minCents: z.int().nonnegative().nullable().default(null),
  maxCents: z.int().nonnegative().nullable().default(null)
}).refine(
  (range) => range.minCents === null || range.maxCents === null || range.minCents <= range.maxCents,
  { message: "the minimum budget cannot exceed the maximum", path: ["minCents"] }
);
var Title = z.string().trim().min(1).max(140);
var Description = z.string().trim().min(1).max(5e3);
var JobAddressInputSchema = ProviderAddressCreateSchema;
function rememberNeedsAddress(input) {
  return input.rememberAddress !== true || input.address !== void 0 && input.address !== null;
}
var REMEMBER_WITHOUT_ADDRESS = {
  message: "rememberAddress needs an address to remember",
  path: ["rememberAddress"]
};
var SavedAddressSchema = z.object({
  address: JobAddressInputSchema.nullable()
});
var JobDraftInputSchema = z.object({
  title: Title.optional(),
  description: Description.optional(),
  categorySlugs: z.array(z.string()).max(20).optional(),
  urgency: JobUrgencySchema.optional(),
  budget: BudgetRangeSchema.optional(),
  address: JobAddressInputSchema.optional(),
  rememberAddress: z.boolean().optional()
}).refine(rememberNeedsAddress, REMEMBER_WITHOUT_ADDRESS);
var JobUpdateInputSchema = z.object({
  title: Title.nullable().optional(),
  description: Description.nullable().optional(),
  categorySlugs: z.array(z.string()).max(20).optional(),
  urgency: JobUrgencySchema.nullable().optional(),
  budget: BudgetRangeSchema.nullable().optional(),
  address: JobAddressInputSchema.nullable().optional(),
  rememberAddress: z.boolean().optional()
}).refine(rememberNeedsAddress, REMEMBER_WITHOUT_ADDRESS);
var JobCancelInputSchema = z.object({
  reason: z.string().trim().min(1).max(500).optional()
});
var JobCategorySchema = z.object({
  slug: z.string(),
  nameEs: z.string(),
  nameEn: z.string(),
  requiresLicence: z.boolean()
});
var JobLocationSchema = z.object({
  city: z.string(),
  province: z.string(),
  postalCode: z.string()
});
var JobSchema = z.object({
  id: z.uuid(),
  status: JobStatusSchema,
  /** `QUOTES` until published otherwise (`W6-T01`). */
  mode: JobModeSchema,
  title: z.string().nullable(),
  description: z.string().nullable(),
  categories: z.array(JobCategorySchema),
  urgency: JobUrgencySchema.nullable(),
  budget: z.object({ minCents: z.int().nullable(), maxCents: z.int().nullable() }),
  location: JobLocationSchema.nullable(),
  publishedAt: z.iso.datetime().nullable(),
  /**
   * Set once, on the transition. Mirrors `publishedAt` for the reason `W4-T01` gave that one: it is
   * the timestamp a list screen needs without joining `audit_record`. The *reason* is not here —
   * that is audit metadata (`JobCancelInputSchema`).
   */
  cancelledAt: z.iso.datetime().nullable(),
  /**
   * `W4-T05` §8. Set once, by `awardJob`, in the settle transaction. Mirrors `cancelledAt` for the
   * same reason (`MEM-2026-09-20-15`): when it happened is a column a list screen needs without a
   * join. Which booking and which quote are audit metadata, not columns here.
   */
  awardedAt: z.iso.datetime().nullable(),
  /**
   * `W4-T09` §8. Set once, by the handshake's redeem, in the transaction that marks it `REDEEMED`.
   * Mirrors `awardedAt` for the same reason (`MEM-2026-09-20-15`): when it happened is a column a list
   * screen needs without a join. Which handshake started it is audit metadata, not a column here.
   */
  startedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});

// src/auction.ts
var AuctionStatusSchema = z.enum(["OPEN", "CLOSED", "AWARDED", "EXPIRED", "CANCELLED"]);
var AuctionEventSchema = z.enum(["CLOSE", "EXPIRE", "AWARD", "CANCEL"]);
var RevealPolicySchema = z.enum(["SEALED", "OPEN"]);
function hasBids(context) {
  return context.bidCount > 0 || {
    reason: "an auction with no bids has nothing to choose from \u2014 it expires instead",
    code: "CONFLICT"
  };
}
function hasNoBids(context) {
  return context.bidCount === 0 || {
    reason: "an auction with bids closes for the client to choose \u2014 it does not expire at close",
    code: "CONFLICT"
  };
}
var auctionMachine = defineMachine({
  name: "auction",
  initial: "OPEN",
  states: ["OPEN", "CLOSED", "AWARDED", "EXPIRED", "CANCELLED"],
  terminal: ["AWARDED", "EXPIRED", "CANCELLED"],
  transitions: [
    /**
     * `closes_at` has passed. Which of these two fires is the bid count, and **zero bids is a
     * normal outcome, not an error** — it has its own rule rather than a failed `CLOSE`.
     */
    { from: "OPEN", on: "CLOSE", to: "CLOSED", guard: hasBids },
    { from: "OPEN", on: "EXPIRE", to: "EXPIRED", guard: hasNoBids },
    /**
     * The client picks a bid — **at any time** (operator, 2026-10-01, reviewing `W6-T06`: the client
     * sees bids as they arrive and may choose while bidding runs). Awarding an `OPEN` auction ends
     * bidding in the same write. ADR-018 rule 3.
     */
    { from: ["OPEN", "CLOSED"], on: "AWARD", to: "AWARDED" },
    /** The award window passed without a choice (`AUCTION_AWARD_WINDOW_DAYS`). */
    { from: "CLOSED", on: "EXPIRE", to: "EXPIRED" },
    /** The job was cancelled — the auction goes with it, in the same transaction. */
    { from: ["OPEN", "CLOSED"], on: "CANCEL", to: "CANCELLED" }
  ]
});
var AuctionPublishInputSchema = z.strictObject({
  durationDays: z.int().positive(),
  /** The ceiling: a bid above it is refused (`W6-T02`). Integer cents, EUR (`money.ts`). */
  maxPriceCents: z.int().positive().optional()
});
var AuctionSchema = z.strictObject({
  id: z.uuid(),
  jobId: z.uuid(),
  status: AuctionStatusSchema,
  revealPolicy: RevealPolicySchema,
  maxPriceCents: z.int().positive().nullable(),
  /** Server time is the truth; a countdown on a screen is decoration. */
  closesAt: z.iso.datetime(),
  closedAt: z.iso.datetime().nullable(),
  cancelledAt: z.iso.datetime().nullable(),
  /** `W6-T03`: the bid the client chose, and when. Null until `AWARDED`. */
  awardedBidId: z.uuid().nullable(),
  awardedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime()
});
var AuctionAwardInputSchema = z.strictObject({ bidId: z.uuid() });
var AuctionPublishedSchema = z.strictObject({
  job: JobSchema,
  auction: AuctionSchema
});

// src/ask.ts
var AskPaymentKindSchema = z.enum(["QUOTE_WINDOW", "AUCTION"]);
var AskPaymentStatusCodeSchema = z.enum([
  "PENDING",
  "PAID",
  "EXPIRED",
  "REFUND_PENDING",
  "REFUNDED",
  "REFUND_FAILED"
]);
var QuoteWindowStatusSchema = z.enum(["OPEN", "DREW", "EMPTY"]);
var askPrice = {
  amountCents: z.int32().positive(),
  currency: z.literal("EUR"),
  taxRateBps: z.int().min(0).max(9999),
  taxInclusive: z.boolean()
};
var AskPricesSchema = z.strictObject({
  chargeEnabled: z.boolean(),
  quoteWindow: z.strictObject({ ...askPrice, size: z.int().positive(), days: z.int().positive() }).nullable(),
  auction: z.strictObject(askPrice).nullable()
});
var PublishCheckoutInputSchema = z.discriminatedUnion("mode", [
  z.strictObject({ mode: z.literal("QUOTES") }),
  AuctionPublishInputSchema.extend({ mode: z.literal("AUCTION") })
]);
var QuoteWindowViewSchema = z.strictObject({
  seq: z.int().positive(),
  status: QuoteWindowStatusSchema,
  openedAt: z.iso.datetime(),
  refundDeadlineAt: z.iso.datetime(),
  decidedAt: z.iso.datetime().nullable(),
  size: z.int().positive(),
  drew: z.int().min(0),
  full: z.boolean(),
  payment: z.strictObject({
    amountCents: z.int32().positive(),
    status: AskPaymentStatusCodeSchema
  })
});
var JobAskSchema = z.strictObject({
  chargeEnabled: z.boolean(),
  pending: z.strictObject({
    kind: AskPaymentKindSchema,
    createdAt: z.iso.datetime()
  }).nullable(),
  windows: z.array(QuoteWindowViewSchema),
  auction: z.strictObject({
    status: AskPaymentStatusCodeSchema,
    amountCents: z.int32().positive()
  }).nullable(),
  holders: z.int().min(0),
  canBuyWindow: z.boolean()
});
var CURRENCY = "EUR";
var INT32_MAX = 2147483647;
var INT32_MIN = -2147483648;
var MoneyError = class extends Error {
  name = "MoneyError";
};
var MoneySchema = z.strictObject({
  amountCents: z.int32(),
  currency: z.literal(CURRENCY)
});
function assertStorable(amountCents, operation) {
  if (!Number.isInteger(amountCents)) {
    throw new MoneyError(
      `${operation}: ${amountCents} is not a whole number of cents. Money is integer cents \u2014 see TODO.md \xA72 rule 4.`
    );
  }
  if (amountCents < INT32_MIN || amountCents > INT32_MAX) {
    throw new MoneyError(
      `${operation}: ${amountCents} is outside the storable range ${INT32_MIN}\u2026${INT32_MAX} (Postgres integer, which is what Prisma Int is).`
    );
  }
}
function money(amountCents, currency = CURRENCY) {
  assertStorable(amountCents, "money");
  return Object.freeze({ amountCents: amountCents + 0, currency });
}
var zero = (currency = CURRENCY) => money(0, currency);
function sameCurrency(a, b, operation) {
  if (a.currency !== b.currency) {
    throw new MoneyError(
      `${operation}: cannot mix ${a.currency} and ${b.currency}. Convert first, explicitly.`
    );
  }
  return a.currency;
}
function assertWholeCount(value, label, operation) {
  if (!Number.isInteger(value)) {
    throw new MoneyError(`${operation}: ${label} must be a whole number, got ${value}.`);
  }
}
function add(a, b) {
  const currency = sameCurrency(a, b, "add");
  const total = a.amountCents + b.amountCents;
  assertStorable(total, `add(${a.amountCents}, ${b.amountCents})`);
  return money(total, currency);
}
function subtract(a, b) {
  const currency = sameCurrency(a, b, "subtract");
  const difference = a.amountCents - b.amountCents;
  assertStorable(difference, `subtract(${a.amountCents}, ${b.amountCents})`);
  return money(difference, currency);
}
var negate = (m) => money(-m.amountCents, m.currency);
function multiply(m, factor) {
  if (!Number.isInteger(factor)) {
    throw new MoneyError(
      `multiply: ${factor} is not a whole quantity. For a rate or a percentage use prorate(m, numerator, denominator) or applyBasisPoints(m, bps), where the rounding rule is explicit.`
    );
  }
  const product = m.amountCents * factor;
  assertStorable(product, `multiply(${m.amountCents}, ${factor})`);
  return money(product, m.currency);
}
function prorate(m, numerator, denominator) {
  assertWholeCount(numerator, "numerator", "prorate");
  assertWholeCount(denominator, "denominator", "prorate");
  if (denominator === 0) {
    throw new MoneyError(`prorate: denominator is 0 (numerator ${numerator}).`);
  }
  const product = m.amountCents * numerator;
  if (!Number.isSafeInteger(product)) {
    throw new MoneyError(
      `prorate: ${m.amountCents} \xD7 ${numerator} exceeds the exact-integer range, so the ratio cannot be applied without losing cents. Reduce the ratio before applying it.`
    );
  }
  const exact = product / denominator;
  const rounded = Math.sign(exact) * Math.round(Math.abs(exact));
  assertStorable(rounded, `prorate(${m.amountCents}, ${numerator}, ${denominator})`);
  return money(rounded, m.currency);
}
var applyBasisPoints = (m, bps) => prorate(m, bps, 1e4);
function allocate(m, weights) {
  if (weights.length === 0) {
    throw new MoneyError("allocate: no weights \u2014 there is nobody to allocate to.");
  }
  for (const weight of weights) {
    assertWholeCount(weight, `weight ${weight}`, "allocate");
    if (weight < 0) {
      throw new MoneyError(`allocate: negative weight ${weight} in [${weights.join(", ")}].`);
    }
  }
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  if (totalWeight === 0) {
    throw new MoneyError(
      `allocate: every weight is 0 in [${weights.join(", ")}] \u2014 the shares would be undefined.`
    );
  }
  const sign = m.amountCents < 0 ? -1 : 1;
  const magnitude = Math.abs(m.amountCents);
  const shares = weights.map((weight) => Math.floor(magnitude * weight / totalWeight));
  const remainders = weights.map(
    (weight, index) => magnitude * weight / totalWeight - (shares[index] ?? 0)
  );
  let leftover = magnitude - shares.reduce((a, b) => a + b, 0);
  const byRemainder = remainders.map((remainder, index) => ({ remainder, index })).sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  for (const { index } of byRemainder) {
    if (leftover === 0) break;
    shares[index] = (shares[index] ?? 0) + 1;
    leftover -= 1;
  }
  return shares.map((share) => money(sign * share, m.currency));
}
function split(m, parts) {
  assertWholeCount(parts, "parts", "split");
  if (parts < 1) {
    throw new MoneyError(`split: ${parts} parts is not a split.`);
  }
  return allocate(
    m,
    Array.from({ length: parts }, () => 1)
  );
}
function sum(items, currency = CURRENCY) {
  return items.reduce((total, item) => add(total, item), zero(currency));
}
function compare(a, b) {
  sameCurrency(a, b, "compare");
  if (a.amountCents < b.amountCents) return -1;
  if (a.amountCents > b.amountCents) return 1;
  return 0;
}
var equals = (a, b) => a.currency === b.currency && a.amountCents === b.amountCents;
var min = (a, b) => compare(a, b) <= 0 ? a : b;
var max = (a, b) => compare(a, b) >= 0 ? a : b;
var isZero = (m) => m.amountCents === 0;
var isPositive = (m) => m.amountCents > 0;
var isNegative = (m) => m.amountCents < 0;
var BPS_TOLERANCE = 1e-6;
function basisPointsOfRate(rate, operation) {
  if (typeof rate !== "number" || !Number.isFinite(rate) || rate < 0 || rate >= 1) {
    throw new MoneyError(
      `${operation}: rate ${String(rate)} is not a fraction in [0, 1). Pass the rate this payment carries (0.21 for 21%) \u2014 there is no default rate (ADR-017 \xA73.1).`
    );
  }
  const scaled = rate * 1e4;
  const bps = Math.round(scaled);
  if (Math.abs(scaled - bps) > BPS_TOLERANCE) {
    throw new MoneyError(
      `${operation}: rate ${rate} is not a whole number of basis points (${scaled}).`
    );
  }
  return bps;
}
function ivaOf(amountCents, rate) {
  return applyBasisPoints(money(amountCents), basisPointsOfRate(rate, "ivaOf")).amountCents;
}
function withIva(amountCents, rate) {
  const base = money(amountCents);
  const iva = applyBasisPoints(base, basisPointsOfRate(rate, "withIva"));
  return { iva: iva.amountCents, total: add(base, iva).amountCents };
}

// src/booking.ts
var BookingStatusSchema = z.enum([
  "PENDING_PAYMENT",
  "PAID",
  "EXPIRED",
  "REFUNDING",
  "REFUNDED",
  "IN_PROGRESS",
  "CANCELLED",
  "NO_SHOW_REFUNDED",
  "COMPLETED"
]);
var PaymentStatusSchema = z.enum([
  "REQUIRES_PAYMENT_METHOD",
  "REQUIRES_ACTION",
  "PROCESSING",
  "SUCCEEDED",
  "CANCELED"
]);
var IvaRateBpsSchema = z.int().min(0).max(9999);
var CreateBookingInputSchema = z.strictObject({});
var BookingSchema = z.object({
  id: z.uuid(),
  quoteId: z.uuid(),
  jobId: z.uuid(),
  status: BookingStatusSchema,
  fee: MoneySchema,
  /** IVA on the platform's fee (`W5-T19`); on a booking created before it, on the whole fee. */
  iva: MoneySchema,
  total: MoneySchema,
  ivaRateBps: IvaRateBpsSchema,
  expiresAt: z.iso.datetime(),
  paidAt: z.iso.datetime().nullable()
});
var BookingPaymentSchema = z.object({
  booking: BookingSchema,
  payment: z.object({
    clientSecret: z.string().min(1),
    publishableKey: z.string().regex(/^pk_(test|live)_/),
    fee: MoneySchema,
    /** `fee − platformFee`: the professional's share, ex IVA. No IVA is charged on it (`W5-T19`). */
    providerShare: MoneySchema,
    /** `floor(fee × platformShareBps / 10 000)`: the platform's share, and the IVA's base (`W5-T19`). */
    platformFee: MoneySchema,
    /** The platform's share of the fee in basis points, frozen on the booking at `booking.create`. */
    platformShareBps: z.int().min(0).max(1e4),
    /** IVA on the platform's fee (`W5-T19`); on a booking created before it, on the whole fee. */
    iva: MoneySchema,
    total: MoneySchema,
    ivaRateBps: IvaRateBpsSchema,
    status: PaymentStatusSchema
  })
});
var CalloutReleaseStateSchema = z.enum([
  "NOT_STARTED",
  "SENDING",
  "WAITING_FOR_STRIPE",
  "SENT"
]);
var CalloutReleaseViewSchema = z.strictObject({
  state: CalloutReleaseStateSchema,
  /** `fee − platformFee`, ex IVA: what the transfer pays the professional. */
  providerShare: MoneySchema.optional(),
  /** The platform's share of the fee, kept by the platform (`W5-T19`'s split). */
  platformFee: MoneySchema.optional(),
  /** When Stripe answered the transfer. */
  sentAt: z.iso.datetime().optional()
});
var AVAILABILITY_MINUTE_GRAIN = 15;
var AVAILABILITY_MINUTES_PER_DAY = 1440;
var AVAILABILITY_MAX_WINDOWS_PER_WEEKDAY = 3;
var AVAILABILITY_MAX_WINDOWS = 21;
var AVAILABILITY_MAX_BLOCKED_DATES = 366;
var AVAILABILITY_HORIZON_DAYS = 366;
var AVAILABILITY_DEFAULT_TIMEZONE = "Europe/Madrid";
var WeekdaySchema = z.int().min(1).max(7);
var MinuteOfDaySchema = z.int().min(0).max(AVAILABILITY_MINUTES_PER_DAY).multipleOf(AVAILABILITY_MINUTE_GRAIN);
var WeeklyWindowSchema = z.strictObject({
  weekday: WeekdaySchema,
  startMinute: MinuteOfDaySchema,
  endMinute: MinuteOfDaySchema
}).refine((window) => window.startMinute < window.endMinute, {
  path: ["endMinute"],
  message: "a window ends after it starts"
});
var BlockedDateSchema = z.iso.date();
var AvailabilityWriteSchema = z.strictObject({
  weeklyHours: z.array(WeeklyWindowSchema).max(AVAILABILITY_MAX_WINDOWS),
  blockedDates: z.array(BlockedDateSchema).max(AVAILABILITY_MAX_BLOCKED_DATES)
}).superRefine((document, ctx) => {
  const byWeekday = /* @__PURE__ */ new Map();
  document.weeklyHours.forEach((window, index) => {
    const day = byWeekday.get(window.weekday) ?? [];
    day.push({ index, start: window.startMinute, end: window.endMinute });
    byWeekday.set(window.weekday, day);
    if (day.length === AVAILABILITY_MAX_WINDOWS_PER_WEEKDAY + 1) {
      ctx.addIssue({
        code: "custom",
        path: ["weeklyHours", index],
        message: `at most ${String(AVAILABILITY_MAX_WINDOWS_PER_WEEKDAY)} windows on one weekday`
      });
    }
  });
  for (const day of byWeekday.values()) {
    const byStart = [...day].sort((a, b) => a.start - b.start || a.index - b.index);
    let reach = Number.NEGATIVE_INFINITY;
    for (const window of byStart) {
      if (window.start <= reach) {
        ctx.addIssue({
          code: "custom",
          path: ["weeklyHours", window.index],
          message: "windows on one weekday must neither overlap nor touch"
        });
      }
      reach = Math.max(reach, window.end);
    }
  }
  const seen = /* @__PURE__ */ new Set();
  document.blockedDates.forEach((date, index) => {
    if (seen.has(date)) {
      ctx.addIssue({
        code: "custom",
        path: ["blockedDates", index],
        message: "this date is already blocked"
      });
    }
    seen.add(date);
  });
});
var AvailabilitySchema = z.strictObject({
  configured: z.boolean(),
  /** IANA name; the stored column, served so a client can say whose 09:00 it is (§3.5). */
  timezone: z.string().min(1),
  weeklyHours: z.array(WeeklyWindowSchema).max(AVAILABILITY_MAX_WINDOWS),
  blockedDates: z.array(BlockedDateSchema).max(AVAILABILITY_MAX_BLOCKED_DATES)
});
var AvailabilityOwnSchema = AvailabilitySchema.extend({
  updatedAt: z.iso.datetime().nullable()
});
var QuoteStatusSchema = z.enum([
  "PENDING",
  "WITHDRAWN",
  "ACCEPTED",
  "REJECTED",
  "AWARDED"
]);
var QuoteStateSchema = z.enum([
  "PENDING",
  "WITHDRAWN",
  "ACCEPTED",
  "REJECTED",
  "EXPIRED",
  "AWARDED"
]);
var QuoteEventSchema = z.enum(["WITHDRAW", "ACCEPT", "REJECT", "AWARD", "UNACCEPT"]);
var quoteMachine = defineMachine({
  name: "quote",
  initial: "PENDING",
  states: ["PENDING", "WITHDRAWN", "ACCEPTED", "REJECTED", "AWARDED"],
  /**
   * Withdrawing is final: the provider submits a *new* quote rather than reinstating an old one,
   * which is also what makes the partial unique index in `0012` safe (`W4-T03` spec §2.4).
   *
   * `ACCEPTED` was here as a debt `W4-T04` declared — terminal only because the award did not exist
   * yet — with the rule that it leaves in the same change that gives it an exit. **Paid by `W4-T05`**:
   * `AWARD` is that exit, and `ACCEPTED` left this list in the same edit (spec §2.5, AC21). `W4-T17`
   * gave it a second, `UNACCEPT`, back to `PENDING`.
   *
   * **`AWARDED` is terminal for now, and this is the next declared debt.** Its only exit is undoing an
   * award after the fee was paid, which is a refund and a forfeit — money's decision, `W5-T05`
   * (spec §2.5, E5) — not a click on a quote. The same rule applies: **`AWARDED` leaves this list in
   * the same change that gives it an exit.**
   */
  terminal: ["WITHDRAWN", "REJECTED", "AWARDED"],
  transitions: [
    { from: "PENDING", on: "WITHDRAW", to: "WITHDRAWN" },
    {
      from: "PENDING",
      on: "ACCEPT",
      to: "ACCEPTED",
      /**
       * An offer past its own validity date cannot be taken up. The rule lives here, next to the
       * states, rather than as an `if` in the repository — `MEM-2026-09-20-13` applied to a guard.
       */
      guard: (context) => context.validUntil.getTime() > context.now.getTime() || {
        reason: "this quote has expired and can no longer be accepted",
        code: "CONFLICT"
      }
    },
    /**
     * **`REJECT` carries no expiry guard, deliberately.** Rejecting a lapsed offer is a client
     * tidying their own screen; refusing it would leave a row nobody can ever clear.
     */
    { from: "PENDING", on: "REJECT", to: "REJECTED" },
    /**
     * `W4-T05` §3.2. Fired only by `awardJob`, on money's settle transaction, actor `SYSTEM`, after
     * the job's own `OPEN --AWARD--> AWARDED`. The losing siblings that are still `PENDING` take the
     * ordinary `REJECT` above, with `metadata.reason = 'JOB_AWARDED'` — no event of their own.
     *
     * **Unguarded, on purpose.** "The job is `OPEN`" is a fact about another row, checked by
     * `awardJob` under the job lock (§2.2 rule 4); a guard here could only read it from a context
     * loaded before the lock. And there is **no expiry guard**: the client accepted inside the
     * validity window, and a payment that settles after it does not un-make that decision —
     * `quoteStatusOf`'s rule that a person's answer outranks the calendar.
     */
    { from: "ACCEPTED", on: "AWARD", to: "AWARDED" },
    /**
     * `W4-T05` §2.7, `W4-T17` §2.1, §3. The client takes back an accept, fired by un-accept only,
     * actor `USER`. Refused while a payment for the quote is in progress (`context.pendingBooking`);
     * that payment expires if it is not completed, and the client can undo then (`W4-T17` §6).
     *
     * **No expiry guard**, as `AWARD` and `REJECT` have none: undoing an accept on a lapsed quote is
     * allowed, and the quote then reads `EXPIRED` through `quoteStatusOf`, because it is `PENDING`
     * again and its window has closed (`W4-T17` AC33).
     *
     * The guard refuses with `CONFLICT` because `RejectionCode` (`state-machine.ts`) is
     * `'CONFLICT' | 'FORBIDDEN'` and stays so. The repository answers this one refusal as the domain
     * code `QUOTE_PAYMENT_PENDING` (ADR-019: a domain code is raised by name, never by the machine).
     */
    {
      from: "ACCEPTED",
      on: "UNACCEPT",
      to: "PENDING",
      guard: (context) => !context.pendingBooking || {
        reason: "a payment for this quote is in progress",
        code: "CONFLICT"
      }
    }
  ]
});
function quoteStatusOf(status, validUntil, now) {
  if (status !== "PENDING") return status;
  return validUntil.getTime() <= now.getTime() ? "EXPIRED" : "PENDING";
}
var QUOTABLE_IN = {
  /** Nobody can see it but its owner, so there is nothing to quote on. */
  DRAFT: false,
  /** The only state whose whole purpose is being found and answered. */
  OPEN: true,
  /** Over. */
  CANCELLED: false,
  /** The job has its provider and has paid to say so. `409 JOB_CLOSED` (`W4-T05` §2.5, AC15). */
  AWARDED: false,
  /** `W4-T09` §2.6. The market closed at `AWARDED`, and the work has started since. */
  IN_PROGRESS: false
};
function canQuoteOn(status) {
  return QUOTABLE_IN[status] || {
    reason: `this job is ${status} and is not accepting quotes`,
    code: "CONFLICT"
  };
}
var QUOTED_IN_MODE = {
  QUOTES: true,
  AUCTION: false
};
function canQuoteInMode(mode) {
  return QUOTED_IN_MODE[mode] || {
    reason: "this job is an auction \u2014 place a bid instead of a quote",
    code: "CONFLICT"
  };
}
var DECIDABLE_IN = {
  /** A draft has no quotes on it at all — nothing may quote one. */
  DRAFT: false,
  /** The only state whose purpose is being answered. */
  OPEN: true,
  /** Over. The quotes stay as history and nobody answers them. */
  CANCELLED: false,
  /**
   * `W4-T05` §2.5, AC16. Every sibling is already `REJECTED` and the winner `AWARDED`, so nothing is
   * left to answer. This also refuses un-accept (§2.7) on an awarded job: un-awarding is `W5-T05`'s
   * refund decision, not a quote click.
   */
  AWARDED: false,
  /** `W4-T09` §2.6. As `AWARDED`: the market closed there, and nothing is left to answer. */
  IN_PROGRESS: false
};
function canDecideOn(status) {
  return DECIDABLE_IN[status] || {
    reason: `this job is ${status}; its quotes can no longer be answered`,
    code: "CONFLICT"
  };
}
var QuoteCoverageSchema = JobCategorySchema.extend({
  /** Whether the provider has this category on their profile. **Not** a permission to quote. */
  listedByProvider: z.boolean()
});
var QuoteProviderSchema = z.strictObject({
  id: z.uuid(),
  displayName: z.string().min(1),
  /** Null is "no reviews yet", which is not 0.00 — the rule `search.ts` states at the column. */
  ratingAvg: z.number().min(0).max(5).nullable(),
  ratingCount: z.number().int().nonnegative(),
  /** Integer cents. Null = quote-only, which is most of the people writing these. */
  hourlyRateCents: z.number().int().nonnegative().nullable()
});
var QUOTE_MIN_AMOUNT_CENTS = 5e3;
var QuoteInputSchema = z.object({
  amountCents: z.int().min(QUOTE_MIN_AMOUNT_CENTS),
  validUntil: z.iso.datetime(),
  breakdown: z.string().trim().min(1).max(5e3).optional()
});
var QuoteUnacceptInputSchema = z.strictObject({});
var QuoteSchema = z.object({
  id: z.uuid(),
  jobId: z.uuid(),
  status: QuoteStateSchema,
  /** Ex IVA. No floor on the way out: quotes written before `W4-T16` may sit under €50 (spec §8). */
  amountCents: z.int().nonnegative(),
  breakdown: z.string().nullable(),
  validUntil: z.iso.datetime(),
  provider: QuoteProviderSchema,
  /** Every category the **job** names, not every category the provider claims. Spec §2.3. */
  coverage: z.array(QuoteCoverageSchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
});
var QUOTE_SORTABLE = ["createdAt"];
var QUOTE_DEFAULT_SORT = "-createdAt";
var QuoteListQuerySchema = listQuery({
  sortable: QUOTE_SORTABLE,
  defaultSort: QUOTE_DEFAULT_SORT
});
var QuotePageSchema = pageEnvelope(QuoteSchema);

// src/bid.ts
var BidStatusSchema = z.enum(["ACTIVE", "REPLACED", "WITHDRAWN"]);
var BidEventSchema = z.enum(["REPLACE", "WITHDRAW"]);
var bidMachine = defineMachine({
  name: "bid",
  initial: "ACTIVE",
  states: ["ACTIVE", "REPLACED", "WITHDRAWN"],
  terminal: ["REPLACED", "WITHDRAWN"],
  transitions: [
    /** The provider bid lower; this one stays as history. */
    { from: "ACTIVE", on: "REPLACE", to: "REPLACED" },
    { from: "ACTIVE", on: "WITHDRAW", to: "WITHDRAWN" }
  ]
});
var BID_MESSAGE_MAX = 2e3;
var BidInputSchema = z.strictObject({
  amountCents: z.int().positive(),
  message: z.string().trim().min(1).max(BID_MESSAGE_MAX).optional()
});
var BidResultSchema = z.enum(["PENDING", "WON", "LOST", "NOT_AWARDED"]);
var RESULT_IN = {
  /** Bidding runs; the client may choose at any moment. */
  OPEN: "PENDING",
  /** Bidding is over; the client is choosing. */
  CLOSED: "PENDING",
  AWARDED: "BY_AWARD",
  EXPIRED: "NOT_AWARDED",
  CANCELLED: "NOT_AWARDED"
};
function bidResultOf(auctionStatus, awardedBidId, bidId) {
  const result = RESULT_IN[auctionStatus];
  if (result !== "BY_AWARD") return result;
  return awardedBidId === bidId ? "WON" : "LOST";
}
var BidSchema = z.strictObject({
  id: z.uuid(),
  auctionId: z.uuid(),
  jobId: z.uuid(),
  status: BidStatusSchema,
  amountCents: z.int().positive(),
  message: z.string().nullable(),
  /** `W6-T03`: how the auction treated it. */
  result: BidResultSchema,
  createdAt: z.iso.datetime()
});
var BidListSchema = z.strictObject({ items: z.array(BidSchema) });
var AuctionBidSchema = z.strictObject({
  id: z.uuid(),
  amountCents: z.int().positive(),
  message: z.string().nullable(),
  provider: QuoteProviderSchema,
  createdAt: z.iso.datetime()
});
var AuctionBidListSchema = z.strictObject({ items: z.array(AuctionBidSchema) });
var CategorySummarySchema = z.strictObject({
  slug: z.string().regex(CATEGORY_SLUG),
  name: z.string().min(1),
  /**
   * The legal flag, surfaced rather than hidden. `W1-T05` §5.5 keeps it explicit per node instead of
   * inherited, so that "which categories need a licence" is one query. A storefront that drops it
   * here cannot render the badge that makes `requiresLicence` mean anything to a visitor.
   */
  requiresLicence: z.boolean()
});
var CategoryListSchema = z.strictObject({
  items: z.array(CategorySummarySchema)
});
var ClientProfileSchema = z.object({
  displayName: z.string().nullable(),
  defaultAddressId: z.guid().nullable()
});
var ClientProfileUpdateSchema = z.strictObject({
  displayName: z.string().trim().min(1).max(80)
});
var SavedAddressCreateSchema = ProviderAddressCreateSchema.extend({
  label: z.string().trim().min(1).max(60)
});
var SavedAddressEntrySchema = ProviderAddressCreateSchema.extend({
  id: z.guid(),
  isDefault: z.boolean()
});
var SavedAddressListSchema = z.object({
  addresses: z.array(SavedAddressEntrySchema)
});
var DefaultAddressInputSchema = z.strictObject({
  addressId: z.guid()
});
var GENERIC_ERROR_CODES = {
  VALIDATION_FAILED: 400,
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  NOT_ACCEPTABLE: 406,
  CONFLICT: 409,
  PAYLOAD_TOO_LARGE: 413,
  UNSUPPORTED_MEDIA_TYPE: 415,
  RATE_LIMITED: 429,
  INTERNAL_ERROR: 500
};
var DOMAIN_ERROR_CODES = {
  /**
   * `W5-T03`: a webhook whose `Stripe-Signature` does not verify — "you are not Stripe", which is a
   * different finding from "your body is wrong" for whoever reads the log. A framework `400` is still
   * `VALIDATION_FAILED`: `codeForStatus` reads only the generic table.
   */
  WEBHOOK_SIGNATURE_INVALID: 400,
  /** `W5-T02` §6: the quote is absent, not on the caller's job, or its id is malformed (ADR-016). */
  QUOTE_NOT_FOUND: 404,
  /** `W5-T02` §6: the booking is absent, the caller is not a party, or its id is malformed. */
  BOOKING_NOT_FOUND: 404,
  /** `W5-T02` §6: the quote is not `ACCEPTED`, or it lapsed, or it was withdrawn. */
  QUOTE_NOT_PAYABLE: 409,
  /** `W5-T02` §6: the job is not `OPEN`. */
  JOB_NOT_AWARDABLE: 409,
  /** `W5-T02` §6: another booking on the job is already `PAID`. */
  JOB_ALREADY_AWARDED: 409,
  /**
   * `W5-T02` §6: Stripe is unreachable, the fee or IVA config is missing, or the call-out flag is off.
   * No generic code claims `503`, and this one must not start answering for it.
   */
  PAYMENTS_UNAVAILABLE: 503,
  /**
   * `W5-T01` §7: a quote or bid by a provider whose payout status is not `READY`, while the gate is on.
   * Raised only on `POST /api/jobs/{id}/quotes` and `POST /api/jobs/{id}/bids`; DMs are never gated.
   */
  PAYOUTS_NOT_READY: 409,
  /**
   * `W5-T13` §4.1: a quote or bid by a provider with no entitled subscription (`ACTIVE`, `TRIALING`,
   * `PAST_DUE`), while `SUBSCRIPTION_GATE_ENABLED` is on. Raised only on `POST /api/jobs/{id}/quotes`
   * and `POST /api/jobs/{id}/bids`, before `PAYOUTS_NOT_READY`; the same status as that sibling.
   */
  SUBSCRIPTION_REQUIRED: 409,
  /**
   * `W5-T08` §4.1, §7: the job has quotes from as many providers as its windows allow, and the caller
   * holds no slot on it. **Not an error of the caller's**: the request is full, and the web shows it as
   * a notice, never as a failure. Raised only on `POST /api/jobs/{id}/quotes`, inside the insert
   * transaction, after `SUBSCRIPTION_REQUIRED` and `PAYOUTS_NOT_READY` (about the provider) and after
   * `CONFLICT` (a closed job); never on bids (§4.2).
   */
  QUOTE_REQUEST_FULL: 409,
  /** `W5-T01` §7: an onboarding link asked for an account Stripe rejected; no link is offered. */
  PAYOUTS_ONBOARDING_CLOSED: 409,
  /**
   * `W4-T17` §6: un-accept refused because a PENDING_PAYMENT booking exists for the quote, expired or
   * not. Raised only on POST /api/quotes/{id}/unaccept.
   */
  QUOTE_PAYMENT_PENDING: 409,
  /** `W4-T09` §6: no handshake with that token; for a short code, no `ISSUED` handshake on that job. */
  HANDSHAKE_NOT_FOUND: 404,
  /** `W4-T09` §6: the caller is not the booking's client, or is its provider — never both parties. */
  HANDSHAKE_WRONG_PARTY: 403,
  /** `W4-T09` §6: the code is past `expires_at`, superseded, or burned by failed attempts. */
  HANDSHAKE_EXPIRED: 409,
  /** `W4-T09` §6: the start is already attested — a `REDEEMED` handshake exists for the booking. */
  HANDSHAKE_ALREADY_REDEEMED: 409,
  /** `W4-T09` §6: a short code that does not match the job's live code; counts a failed attempt. */
  HANDSHAKE_CODE_INVALID: 400,
  /** `W4-T09` §6: issue on a job not `AWARDED` or a booking not `PAID`; redeem the port refused. */
  HANDSHAKE_NOT_AVAILABLE: 409,
  /** `W5-T07` §7: checkout asked for by a provider who already has a live subscription. */
  SUBSCRIPTION_EXISTS: 409,
  /** `W5-T07` §7: the portal asked for by a provider with no Stripe customer yet. */
  SUBSCRIPTION_NOT_FOUND: 409,
  /** `W5-T07` §7: a plan switch while the subscription is not `ACTIVE`, or its cancellation is scheduled. */
  SUBSCRIPTION_NOT_CHANGEABLE: 409,
  /**
   * `W5-T07` §7: an upgrade's prorated charge failed (Stripe answered a `pending_update`); nothing
   * changed. The only code at `402`, and no generic code claims it, so a framework `402` stays
   * unmatched (ADR-019).
   */
  SUBSCRIPTION_PAYMENT_FAILED: 402,
  /**
   * `W5-T07` §7, §10.1: `PUT /api/providers/me` changing `kind` while a live subscription exists. The
   * kind moves the price, so it changes only through `POST /api/me/subscription/plan`.
   */
  SUBSCRIPTION_KIND_LOCKED: 409,
  /*
   * `W5-T12` §4.9 / §7 — charge to ask. Each of the four is **the job's state, not an error of the
   * caller's**: the request was well formed, and the job is not (or not yet) in a state that takes it.
   * The web shows each as a notice or a next step, never as a failure. Raised only by name.
   */
  /**
   * `W5-T12` §4.5: `POST /api/jobs/{id}/publish` or `POST /api/jobs/{id}/auction` while
   * `ASK_CHARGE_ENABLED` is on — publishing needs a payment (`/publish/checkout`). Answered after the
   * ownership and draft checks (a stranger still gets `404`, a published job `409 CONFLICT`), before any
   * write.
   */
  ASK_PAYMENT_REQUIRED: 409,
  /**
   * `W5-T12` §4.3 step 4: the job's pending payment is already complete at Stripe and its webhook has
   * not landed yet. Nothing is charged twice; the page polls until the job moves.
   */
  ASK_PAYMENT_CONFIRMING: 409,
  /**
   * `W5-T12` §4.4 (E4 = A): another window was asked for while one still has room. Another opens only
   * when none has room, which is what keeps "did this window draw a quote?" exact (§3.2).
   */
  QUOTE_WINDOW_HAS_ROOM: 409,
  /**
   * `W5-T12` §3.3 (E8, E12 = A): a provider who holds no slot quotes a job whose every window was
   * refunded empty. A refund ends what was bought; the request takes quotes again only if the client
   * buys another window. Raised only on `POST /api/jobs/{id}/quotes`, where `QUOTE_REQUEST_FULL` is; a
   * slot holder is never refused by it.
   */
  QUOTE_REQUEST_CLOSED: 409,
  /**
   * `W5-T09` §4.5, §7: `GET /api/documents/{id}` and its `/pdf`. One answer for a document that does
   * not exist, is not the caller's (only its recipient may read it, §6), or has a malformed id
   * (ADR-016), so a prober learns nothing about which ids are real.
   */
  DOCUMENT_NOT_FOUND: 404
};
var ERROR_CODES = { ...GENERIC_ERROR_CODES, ...DOMAIN_ERROR_CODES };
var DETAIL_SCHEMAS = {
  VALIDATION_FAILED: z.object({
    issues: z.array(z.object({ path: z.string(), message: z.string() }))
  }),
  RATE_LIMITED: z.object({ retryAfterSeconds: z.number().int().nonnegative() })
};
var BASE = {
  /** For humans and logs. **Never displayed** — the web app translates from `code`. */
  message: z.string().min(1),
  /** Always present, always equal to the `x-request-id` response header. */
  requestId: z.string().min(1)
};
var withDetails = (code, details) => z.strictObject({ code: z.literal(code), ...BASE, details: details.optional() });
var withoutDetails = (code) => z.strictObject({ code: z.literal(code), ...BASE });
var ErrorBodySchema = z.discriminatedUnion("code", [
  withDetails("VALIDATION_FAILED", DETAIL_SCHEMAS.VALIDATION_FAILED),
  withDetails("RATE_LIMITED", DETAIL_SCHEMAS.RATE_LIMITED),
  withoutDetails("UNAUTHENTICATED"),
  withoutDetails("FORBIDDEN"),
  withoutDetails("NOT_FOUND"),
  withoutDetails("METHOD_NOT_ALLOWED"),
  withoutDetails("NOT_ACCEPTABLE"),
  withoutDetails("CONFLICT"),
  withoutDetails("PAYLOAD_TOO_LARGE"),
  withoutDetails("UNSUPPORTED_MEDIA_TYPE"),
  withoutDetails("INTERNAL_ERROR"),
  withoutDetails("WEBHOOK_SIGNATURE_INVALID"),
  // `W1-T11`. None carries details: a Stripe message or id must never reach the wire (W5-T02 AC 32).
  withoutDetails("QUOTE_NOT_FOUND"),
  withoutDetails("BOOKING_NOT_FOUND"),
  withoutDetails("QUOTE_NOT_PAYABLE"),
  withoutDetails("JOB_NOT_AWARDABLE"),
  withoutDetails("JOB_ALREADY_AWARDED"),
  withoutDetails("PAYMENTS_UNAVAILABLE"),
  // `W5-T01`. Neither carries details: a requirement name or a Stripe id must never reach the wire.
  withoutDetails("PAYOUTS_NOT_READY"),
  withoutDetails("PAYOUTS_ONBOARDING_CLOSED"),
  // `W5-T13`. No details: the subscription's status goes to the log and the metric, never the wire.
  withoutDetails("SUBSCRIPTION_REQUIRED"),
  // `W5-T08`. No details: the holder count and the capacity go to the log and the metric, never the
  // wire — "full" is the one bit about other quotes a provider is told (`job-feed.ts`, E4).
  withoutDetails("QUOTE_REQUEST_FULL"),
  // `W4-T17`. No details: whether a payment is in progress is all the client is told.
  withoutDetails("QUOTE_PAYMENT_PENDING"),
  // `W4-T09`. None carries details: never the token, the code, the attempt count or a booking id.
  withoutDetails("HANDSHAKE_NOT_FOUND"),
  withoutDetails("HANDSHAKE_WRONG_PARTY"),
  withoutDetails("HANDSHAKE_EXPIRED"),
  withoutDetails("HANDSHAKE_ALREADY_REDEEMED"),
  withoutDetails("HANDSHAKE_CODE_INVALID"),
  withoutDetails("HANDSHAKE_NOT_AVAILABLE"),
  // `W5-T07`. None carries details: never a Stripe id, a price or a card fact.
  withoutDetails("SUBSCRIPTION_EXISTS"),
  withoutDetails("SUBSCRIPTION_NOT_FOUND"),
  withoutDetails("SUBSCRIPTION_NOT_CHANGEABLE"),
  withoutDetails("SUBSCRIPTION_PAYMENT_FAILED"),
  withoutDetails("SUBSCRIPTION_KIND_LOCKED"),
  // `W5-T12`. None carries details: an amount, a holder count or a Stripe id goes to the log and the
  // metric, never the wire.
  withoutDetails("ASK_PAYMENT_REQUIRED"),
  withoutDetails("ASK_PAYMENT_CONFIRMING"),
  withoutDetails("QUOTE_WINDOW_HAS_ROOM"),
  withoutDetails("QUOTE_REQUEST_CLOSED"),
  // `W5-T09`. No details: never a number, a recipient or a figure on the wire.
  withoutDetails("DOCUMENT_NOT_FOUND")
]);
var ErrorEnvelopeSchema = z.strictObject({ error: ErrorBodySchema });
var INTERNAL_ERROR_MESSAGE = "An unexpected error occurred.";
function errorEnvelope(code, message, requestId, details) {
  return {
    error: {
      code,
      message,
      requestId,
      // Spread rather than assign: the caller keeps ownership of its object, and a later mutation
      // of it must not rewrite an envelope that has already been built.
      ...details === void 0 ? {} : { details: { ...details } }
    }
  };
}
var AppError = class extends Error {
  constructor(code, message, details) {
    super(message);
    this.code = code;
    this.statusCode = ERROR_CODES[code];
    this.details = details;
  }
  code;
  name = "AppError";
  statusCode;
  details;
  /**
   * The handler has an `AppError` whose `code` is only known at runtime, so it cannot call
   * `errorEnvelope` and keep the per-code typing. This method is where that knowledge already
   * lives — the pairing was checked at the throw site.
   */
  toEnvelope(requestId) {
    return errorEnvelope(this.code, this.message, requestId, this.details);
  }
};
function codeForStatus(status) {
  const match = Object.entries(GENERIC_ERROR_CODES).find(([, value]) => value === status);
  if (match) return { code: match[0], matched: true };
  return {
    code: status >= 400 && status < 500 ? "VALIDATION_FAILED" : "INTERNAL_ERROR",
    matched: false
  };
}
var HANDSHAKE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
var HANDSHAKE_SHORT_CODE_PATTERN = /^[0-9]{6}$/;
var Token = z.string().regex(HANDSHAKE_TOKEN_PATTERN);
var ShortCode = z.string().regex(HANDSHAKE_SHORT_CODE_PATTERN);
var HandshakeStatusSchema = z.enum(["ISSUED", "REDEEMED", "SUPERSEDED"]);
var HandshakeStateSchema = z.strictObject({
  state: z.enum(["NONE", "ISSUED", "EXPIRED", "REDEEMED"]),
  expiresAt: z.iso.datetime().optional(),
  redeemedAt: z.iso.datetime().optional()
});
var HandshakeIssuedSchema = z.strictObject({
  handshakeId: z.uuid(),
  token: Token,
  shortCode: ShortCode,
  url: z.url(),
  expiresAt: z.iso.datetime()
});
var HandshakeRedeemInputSchema = z.union([
  z.strictObject({ token: Token }),
  z.strictObject({ jobId: z.uuid(), shortCode: ShortCode })
]);
var HandshakeRedeemedSchema = z.strictObject({
  jobId: z.uuid(),
  bookingId: z.uuid(),
  startedAt: z.iso.datetime()
});
var JobFeedCoverageSchema = QuoteCoverageSchema;
var JobFeedMyQuoteSchema = z.strictObject({
  id: z.uuid(),
  status: QuoteStateSchema
});
var JobFeedItemSchema = z.strictObject({
  id: z.uuid(),
  /** Presupuestos or auction — which form the provider answers with (`W6-T01` §3.6). */
  mode: JobModeSchema,
  /** Whatever the client typed, which may be nothing at all — `W4-T01` demands no title. */
  title: z.string().nullable(),
  description: z.string().nullable(),
  /** Every trade the job asks for, in taxonomy order. */
  categories: z.array(JobCategorySchema),
  urgency: JobUrgencySchema.nullable(),
  budget: z.object({ minCents: z.int().nullable(), maxCents: z.int().nullable() }),
  /** City, province and postal code. Never a point — see the note above. */
  location: JobLocationSchema,
  /**
   * How far the work is from the provider's own base, in metres, rounded.
   *
   * Rounded in SQL rather than here, for the reason `search.ts` states: the number that goes into a
   * cursor and the number a caller compares must be the same one. It is not a sort key
   * (`JOB_FEED_SORTABLE`) — everything in this list is already inside a distance the provider chose.
   */
  distanceMetres: z.int().nonnegative(),
  /** What the job needs, and which of it this provider lists. Labels, not permissions. */
  coverage: z.array(JobFeedCoverageSchema),
  /**
   * `W6-T02`: what a provider needs to bid at all — the deadline and the client's ceiling — or
   * `null` for a presupuestos job. **Nothing about other bids**: the auction is sealed.
   *
   * `W6-T08` adds `status`, so the provider's bid panel can say *adjudicada* after the client
   * awards early (ADR-018). A status is a fact about the auction, not about another bid, so sealed
   * still holds.
   */
  auction: z.strictObject({
    closesAt: z.iso.datetime(),
    maxPriceCents: z.int().positive().nullable(),
    status: AuctionStatusSchema
  }).nullable(),
  /** The reader's own quote, or `null`. */
  myQuote: JobFeedMyQuoteSchema.nullable(),
  /**
   * `W5-T08` §3, §4.3 (E4 = A, operator): whether the request is full **for the reader**. `true` only
   * when the job is an `OPEN` presupuestos job that already has quotes from as many providers as its
   * windows allow, and the reader holds no slot on it — i.e. only when a quote from the reader would
   * be refused `409 QUOTE_REQUEST_FULL`. `false` for an auction (bids are not capped) and for a job
   * the reader holds a slot on.
   *
   * The one exception to *nothing about the other quotes* above, and deliberately a bit: never the
   * number of holders, the capacity or what is left, never a price, never who. A full row stays in
   * the feed, marked — label, do not hide, as with coverage and `myQuote`.
   */
  full: z.boolean(),
  /** When it entered the feed, which is the order the feed pages in. */
  publishedAt: z.iso.datetime(),
  createdAt: z.iso.datetime()
});
var JOB_FEED_SORTABLE = ["publishedAt"];
var JOB_FEED_DEFAULT_SORT = "-publishedAt";
var JobFeedQuerySchema = listQuery({
  sortable: JOB_FEED_SORTABLE,
  defaultSort: JOB_FEED_DEFAULT_SORT
});
var JobFeedPageSchema = pageEnvelope(JobFeedItemSchema);
var LISTING_MAX_PER_PROVIDER = 20;
var LISTING_TITLE_MIN = 3;
var LISTING_TITLE_MAX = 100;
var LISTING_DESCRIPTION_MAX = 2e3;
var ListingIdSchema = z.uuid();
var ListingPriceModelSchema = z.enum(["HOURLY", "FIXED", "QUOTE_ONLY"]);
var PriceCentsSchema = z.int32().positive();
var ListingWriteSchema = z.strictObject({
  categorySlug: z.string().regex(CATEGORY_SLUG),
  title: z.string().trim().min(LISTING_TITLE_MIN).max(LISTING_TITLE_MAX),
  description: z.string().trim().max(LISTING_DESCRIPTION_MAX).nullable().default(null),
  priceModel: ListingPriceModelSchema,
  /** `null` for `QUOTE_ONLY`, and only then. */
  priceCents: PriceCentsSchema.nullable().default(null),
  active: z.boolean().default(true)
}).superRefine((listing, ctx) => {
  const quoteOnly = listing.priceModel === "QUOTE_ONLY";
  if (quoteOnly && listing.priceCents !== null) {
    ctx.addIssue({
      code: "custom",
      path: ["priceCents"],
      message: "a quote-only listing carries no price"
    });
  } else if (!quoteOnly && listing.priceCents === null) {
    ctx.addIssue({
      code: "custom",
      path: ["priceCents"],
      message: "an hourly or fixed listing needs a price"
    });
  }
});
var ListingSchema = z.strictObject({
  id: ListingIdSchema,
  providerId: ProviderIdSchema,
  categorySlug: z.string().regex(CATEGORY_SLUG),
  title: z.string(),
  description: z.string().nullable(),
  priceModel: ListingPriceModelSchema,
  priceCents: PriceCentsSchema.nullable(),
  /** ISO-8601 UTC, the convention `ProviderProfileSchema.memberSince` set. */
  updatedAt: z.iso.datetime()
});
var ListingOwnSchema = ListingSchema.extend({
  active: z.boolean(),
  createdAt: z.iso.datetime()
});
var ListingListSchema = z.object({
  items: z.array(ListingSchema).max(LISTING_MAX_PER_PROVIDER)
});
var ListingOwnListSchema = z.object({
  items: z.array(ListingOwnSchema).max(LISTING_MAX_PER_PROVIDER)
});
var MoneyDocumentKindSchema = z.enum(["PLATFORM_FEE_RECEIPT", "PAYOUT_STATEMENT"]);
var ReceiptTypeSchema = z.enum(["receipt", "simplified_invoice"]);
var DocumentNumberSchema = z.string().regex(/^[A-Z0-9-]{1,16}-\d{4}-\d{6,}$/);
var RateBpsSchema = z.int().min(0);
var MoneyDocumentIssuerSchema = z.strictObject({
  name: z.string().nullable(),
  taxId: z.string().nullable(),
  address: z.string().nullable()
});
var MoneyDocumentRecipientSchema = z.strictObject({
  name: z.string().nullable()
});
var MoneyDocumentPaymentSchema = z.strictObject({
  /** `booking.fee_cents`: the call-out fee, ex IVA. */
  fee: MoneySchema,
  /** The professional's share. No IVA is charged on it by the platform (E14 = C). */
  providerShare: MoneySchema,
  /** The platform's share of the fee: the IVA's base. */
  platformFee: MoneySchema,
  /** `booking.iva_cents`: the IVA on the platform's fee, copied (A10). */
  iva: MoneySchema,
  /** `booking.iva_rate_bps`. */
  ivaRateBps: RateBpsSchema,
  /** `booking.total_cents`: `fee + iva`. */
  clientPaid: MoneySchema
});
var documentShape = {
  id: z.uuid(),
  /** The stored display number, never re-formatted (§9). */
  number: DocumentNumberSchema,
  /** When the document was issued, by the application clock. */
  issuedAt: z.iso.datetime(),
  /** When the money moved: the ledger entry's `occurred_at`. */
  occurredAt: z.iso.datetime(),
  provisional: z.literal(true),
  /** Which written-down assumptions it was built under (§1.2), e.g. `provisional-2026-10-r4`. */
  assumptionsVersion: z.string().min(1),
  issuer: MoneyDocumentIssuerSchema,
  recipient: MoneyDocumentRecipientSchema,
  bookingId: z.uuid(),
  jobId: z.uuid(),
  payment: MoneyDocumentPaymentSchema
};
var PlatformFeeReceiptSchema = z.strictObject({
  kind: z.literal("PLATFORM_FEE_RECEIPT"),
  ...documentShape,
  base: MoneySchema,
  iva: MoneySchema,
  total: MoneySchema,
  ivaRateBps: RateBpsSchema,
  receiptType: ReceiptTypeSchema
});
var PayoutStatementSchema = z.strictObject({
  kind: z.literal("PAYOUT_STATEMENT"),
  ...documentShape,
  transferred: MoneySchema,
  // `.regex`, never `.startsWith`: zod ≥ 4.6 renders the latter as a format the harness's strict
  // ajv refuses (`MEM-2026-10-24-contracts-1`).
  transferRef: z.string().regex(/^tr_/)
});
var MoneyDocumentSchema = z.discriminatedUnion("kind", [
  PlatformFeeReceiptSchema,
  PayoutStatementSchema
]);
var MoneyDocumentSummarySchema = z.strictObject({
  id: z.uuid(),
  kind: MoneyDocumentKindSchema,
  number: DocumentNumberSchema,
  issuedAt: z.iso.datetime(),
  bookingId: z.uuid(),
  total: MoneySchema
});
var MONEY_DOCUMENT_SORTABLE = ["issuedAt"];
var MONEY_DOCUMENT_DEFAULT_SORT = "-issuedAt";
var MONEY_DOCUMENT_PAGE_LIMIT_MAX = 50;
var MoneyDocumentListQuerySchema = listQuery({
  sortable: MONEY_DOCUMENT_SORTABLE,
  defaultSort: MONEY_DOCUMENT_DEFAULT_SORT,
  limitMax: MONEY_DOCUMENT_PAGE_LIMIT_MAX,
  filters: { bookingId: z.uuid().optional() }
});
var MoneyDocumentPageSchema = pageEnvelope(MoneyDocumentSummarySchema);
var MONEY_DOCUMENT_LANGS = ["es", "en"];
var MoneyDocumentPdfQuerySchema = z.strictObject({
  lang: z.enum(MONEY_DOCUMENT_LANGS).optional()
});
var PayoutStatusCodeSchema = z.enum([
  "NOT_STARTED",
  "INCOMPLETE",
  "IN_REVIEW",
  "ACTION_REQUIRED",
  "REJECTED",
  "READY"
]);
var PayoutStatusSchema = z.strictObject({
  status: PayoutStatusCodeSchema,
  payoutsEnabled: z.boolean(),
  canQuote: z.boolean()
});
var PayoutOnboardingLinkInputSchema = z.strictObject({
  from: z.enum(["registration", "account", "quote", "bid"])
});
var PayoutOnboardingLinkSchema = z.strictObject({
  url: z.url()
});
var StripeWebhookAckSchema = z.strictObject({ received: z.literal(true) });
var StripeEventEnvelopeSchema = z.looseObject({
  // A prefix as a `regex`, not `.startsWith`: zod 4.6 renders that as `format: "starts_with"`, which no
  // JSON Schema validator knows and the contract harness's strict ajv refuses (`W5-T19` step 2).
  id: z.string().regex(/^evt_/),
  object: z.literal("event"),
  type: z.string(),
  livemode: z.boolean(),
  api_version: z.string().nullable(),
  created: z.number().int(),
  account: z.string().optional(),
  data: z.looseObject({ object: z.looseObject({}) })
});
var SubscriptionStatusCodeSchema = z.enum([
  "INCOMPLETE",
  "INCOMPLETE_EXPIRED",
  "TRIALING",
  "ACTIVE",
  "PAST_DUE",
  "UNPAID",
  "CANCELED",
  "PAUSED"
]);
var ProviderSubscriptionSchema = z.strictObject({
  status: z.union([SubscriptionStatusCodeSchema, z.literal("NONE")]),
  plan: ProviderKindSchema.nullable(),
  cancelAtPeriodEnd: z.boolean(),
  currentPeriodEnd: z.iso.datetime().nullable(),
  kindMismatch: z.boolean(),
  entitled: z.boolean()
});
var SubscriptionPlanSchema = z.strictObject({
  kind: ProviderKindSchema,
  amountCents: z.int32().positive(),
  currency: z.literal("EUR"),
  interval: z.literal("month"),
  taxRateBps: z.int().min(0).max(9999).nullable(),
  taxInclusive: z.boolean()
});
var SubscriptionPlansSchema = z.strictObject({
  plans: z.array(SubscriptionPlanSchema).length(2)
});
var SubscriptionCheckoutInputSchema = z.strictObject({
  from: z.enum(["registration", "account", "quote", "bid"])
});
var SubscriptionPlanInputSchema = z.strictObject({
  kind: ProviderKindSchema
});
var HostedRedirectSchema = z.strictObject({
  url: z.url()
});
var MessageSideSchema = z.enum(["CLIENT", "PROVIDER"]);
var MESSAGE_BODY_MAX = 2e3;
var JobMessageInputSchema = z.object({
  body: z.string().trim().min(1).max(MESSAGE_BODY_MAX)
});
var JobMessageSchema = z.strictObject({
  id: z.uuid(),
  threadId: z.uuid(),
  from: MessageSideSchema,
  body: z.string(),
  createdAt: z.iso.datetime()
});
var JobThreadSchema = z.strictObject({
  id: z.uuid(),
  jobId: z.uuid(),
  /** The profile that opened it — the name the client sees, as on a quote. */
  provider: z.strictObject({ id: z.uuid(), displayName: z.string().min(1) }),
  createdAt: z.iso.datetime(),
  lastMessageAt: z.iso.datetime()
});
var JobThreadSummarySchema = JobThreadSchema.extend({
  lastMessage: JobMessageSchema,
  messageCount: z.int().positive()
});
var THREAD_VIEW_LIMIT = 100;
var THREAD_LIST_LIMIT = 100;
var JobThreadViewSchema = z.strictObject({
  /** Null for a provider who may open a thread and has not written yet. */
  thread: JobThreadSchema.nullable(),
  messages: z.array(JobMessageSchema).max(THREAD_VIEW_LIMIT),
  /** True when older messages exist than the ones returned. Loading them is out of scope. */
  hasEarlier: z.boolean(),
  /** Whether this caller may write now — false on a cancelled job. The screen hides the form. */
  canWrite: z.boolean()
});
var JobThreadListSchema = z.strictObject({
  items: z.array(JobThreadSummarySchema).max(THREAD_LIST_LIMIT)
});
var OPENABLE_IN = {
  /** Nobody but its owner can see a draft, so nobody can ask about it. */
  DRAFT: false,
  OPEN: true,
  /** Over. Nobody starts a conversation about a job that is not happening. */
  CANCELLED: false,
  /** The market has closed: the job has its provider, and a stranger has nothing left to ask. */
  AWARDED: false,
  /** `W4-T09` §2.6. As `AWARDED`: no new strangers once the work has started. */
  IN_PROGRESS: false
};
var WRITABLE_IN = {
  /** Unreachable — a draft has no thread — and refused anyway. */
  DRAFT: false,
  OPEN: true,
  /** The conversation stays as history, readable by both, and closed to new messages. */
  CANCELLED: false,
  /**
   * **Must** stay writable: this is when client and provider agree a date (`W4-T05` §2.5). Only
   * the threads that already exist reach this — `OPENABLE_IN.AWARDED` stops a new one.
   */
  AWARDED: true,
  /**
   * `W4-T09` §2.6. **Must** stay writable too: client and professional are working together, and
   * closing their thread now would push them to WhatsApp. `OPENABLE_IN.IN_PROGRESS` stops a new one.
   */
  IN_PROGRESS: true
};
function canOpenThreadOn(status) {
  return OPENABLE_IN[status] || {
    reason: `this job is ${status}; a new conversation cannot be started on it`,
    code: "CONFLICT"
  };
}
function canWriteThreadOn(status) {
  return WRITABLE_IN[status] || {
    reason: `this job is ${status}; its conversations are closed`,
    code: "CONFLICT"
  };
}
var BINARY_MEDIA_TYPES = ["application/pdf"];
function binaryBody(mediaType) {
  return Object.freeze({ binary: mediaType });
}
function isBinaryBody(value) {
  if (typeof value !== "object" || value === null || value instanceof z.ZodType) return false;
  const keys = Object.keys(value);
  const media = value.binary;
  return keys.length === 1 && typeof media === "string" && BINARY_MEDIA_TYPES.includes(media);
}
var PDF_BODY = binaryBody("application/pdf");
var RegistryError = class extends Error {
  constructor(code, message) {
    super(`${code}: ${message}`);
    this.code = code;
    this.name = "RegistryError";
  }
  code;
};
function defineRoute(route) {
  return route;
}
function templateNames(path) {
  return [...path.matchAll(/\{([^}]+)\}/g)].map((match) => match[1] ?? "");
}
function validateRoutes(routes) {
  const byId = /* @__PURE__ */ new Map();
  const byOperation = /* @__PURE__ */ new Map();
  for (const route of routes) {
    const sameId = byId.get(route.id);
    if (sameId !== void 0) {
      throw new RegistryError(
        "REGISTRY_DUPLICATE_ROUTE",
        `"${route.id}" is declared twice (${sameId.method} ${sameId.path} and ${route.method} ${route.path})`
      );
    }
    byId.set(route.id, route);
    const operation = `${route.method} ${route.path}`;
    const sameOperation = byOperation.get(operation);
    if (sameOperation !== void 0) {
      throw new RegistryError(
        "REGISTRY_DUPLICATE_ROUTE",
        `"${sameOperation.id}" and "${route.id}" both declare ${operation}`
      );
    }
    byOperation.set(operation, route);
    const inPath = templateNames(route.path).sort();
    const inParams = Object.keys(route.params?.shape ?? {}).sort();
    if (inPath.join("\n") !== inParams.join("\n")) {
      throw new RegistryError(
        "REGISTRY_PARAM_MISMATCH",
        `"${route.id}": the path ${route.path} names {${inPath.join(", ")}} but params declares {${inParams.join(", ")}}`
      );
    }
  }
}
var IdParams = z.strictObject({ id: z.guid() });
var ThreadParams = z.strictObject({ id: z.guid(), threadId: z.guid() });
var AddressParams = z.strictObject({ addressId: z.guid() });
var ListingParams = z.strictObject({ id: ListingIdSchema });
var ROUTES = [
  // ── Discovery ────────────────────────────────────────────────────────────────────────────
  defineRoute({
    id: "search.providers",
    method: "GET",
    path: "/api/search",
    summary: "Search providers around a place, paged by cursor.",
    tags: ["search"],
    auth: "public",
    query: SearchQuerySchema,
    responses: { 200: SearchResponseSchema },
    errors: []
  }),
  defineRoute({
    id: "category.list",
    method: "GET",
    path: "/api/categories",
    summary: "List the trades a visitor can pick, in the curated order.",
    tags: ["categories"],
    auth: "public",
    responses: { 200: CategoryListSchema },
    errors: []
  }),
  // ── Providers ────────────────────────────────────────────────────────────────────────────
  defineRoute({
    id: "provider.get",
    method: "GET",
    path: "/api/providers/{id}",
    summary: "Read a provider's public profile.",
    tags: ["providers"],
    auth: "public",
    params: z.strictObject({ id: ProviderIdSchema }),
    responses: { 200: ProviderProfileSchema },
    // The one route that answers a malformed id `400` rather than `404` (`W3-T07` §2.3).
    errors: ["NOT_FOUND", "VALIDATION_FAILED"]
  }),
  defineRoute({
    id: "provider.getOwn",
    method: "GET",
    path: "/api/providers/me",
    summary: "Read the signed-in provider's own profile.",
    tags: ["providers"],
    auth: "session",
    responses: { 200: ProviderProfileOwnSchema },
    errors: ["NOT_FOUND"]
  }),
  defineRoute({
    id: "provider.updateOwn",
    method: "PUT",
    path: "/api/providers/me",
    summary: "Replace the signed-in provider's own profile.",
    tags: ["providers"],
    auth: "session",
    body: ProviderProfileUpdateSchema,
    responses: { 200: ProviderProfileSchema },
    // `SUBSCRIPTION_KIND_LOCKED`: `kind` moves the price, so while a live subscription exists it
    // changes only through `subscription.changePlan` (`W5-T07` §6, §10.1).
    errors: ["SUBSCRIPTION_KIND_LOCKED"]
  }),
  defineRoute({
    id: "provider.createOwn",
    method: "POST",
    path: "/api/providers/me",
    summary: "Become a provider: create the signed-in account\u2019s provider profile.",
    tags: ["providers"],
    auth: "session",
    body: ProviderProfileCreateSchema,
    responses: { 201: ProviderProfileSchema },
    errors: ["CONFLICT"]
  }),
  // ── Listings (`W3-T04`) ──────────────────────────────────────────────────────────────────
  defineRoute({
    id: "listing.listOwn",
    method: "GET",
    path: "/api/providers/me/listings",
    summary: "List the signed-in provider's own listings, active or not.",
    tags: ["listings"],
    auth: "session",
    responses: { 200: ListingOwnListSchema },
    errors: []
  }),
  defineRoute({
    id: "listing.createOwn",
    method: "POST",
    path: "/api/providers/me/listings",
    summary: "Create a listing for the signed-in provider.",
    tags: ["listings"],
    auth: "session",
    body: ListingWriteSchema,
    responses: { 201: ListingOwnSchema },
    // The 21st listing. CONFLICT rather than a code of its own: `W1-T01` AC3 gives each status one.
    errors: ["CONFLICT"]
  }),
  defineRoute({
    id: "listing.getOwn",
    method: "GET",
    path: "/api/providers/me/listings/{id}",
    summary: "Read one of the signed-in provider's own listings.",
    tags: ["listings"],
    auth: "session",
    params: ListingParams,
    responses: { 200: ListingOwnSchema },
    // A malformed id is `400` here (`W3-T04` AC19); a stranger's listing is `404`, never `403`.
    errors: ["NOT_FOUND", "VALIDATION_FAILED"]
  }),
  defineRoute({
    id: "listing.updateOwn",
    method: "PUT",
    path: "/api/providers/me/listings/{id}",
    summary: "Replace one of the signed-in provider's own listings.",
    tags: ["listings"],
    auth: "session",
    params: ListingParams,
    body: ListingWriteSchema,
    responses: { 200: ListingOwnSchema },
    errors: ["NOT_FOUND", "VALIDATION_FAILED"]
  }),
  defineRoute({
    id: "listing.deleteOwn",
    method: "DELETE",
    path: "/api/providers/me/listings/{id}",
    summary: "Delete one of the signed-in provider's own listings.",
    tags: ["listings"],
    auth: "session",
    params: ListingParams,
    responses: { 204: null },
    errors: ["NOT_FOUND", "VALIDATION_FAILED"]
  }),
  defineRoute({
    id: "listing.get",
    method: "GET",
    path: "/api/listings/{id}",
    summary: "Read one active listing.",
    tags: ["listings"],
    auth: "public",
    params: ListingParams,
    responses: { 200: ListingSchema },
    errors: ["NOT_FOUND", "VALIDATION_FAILED"]
  }),
  defineRoute({
    id: "listing.listForProvider",
    method: "GET",
    path: "/api/providers/{id}/listings",
    summary: "List a provider's active listings.",
    tags: ["listings"],
    auth: "public",
    params: z.strictObject({ id: ProviderIdSchema }),
    responses: { 200: ListingListSchema },
    // A malformed provider id is `400`, as on `provider.get`; an unknown one is `404`.
    errors: ["NOT_FOUND", "VALIDATION_FAILED"]
  }),
  // ── Availability (`W3-T09`) ──────────────────────────────────────────────────────────────
  defineRoute({
    id: "availability.getOwn",
    method: "GET",
    path: "/api/providers/me/availability",
    summary: "Read the signed-in provider's own availability calendar.",
    tags: ["availability"],
    auth: "session",
    responses: { 200: AvailabilityOwnSchema },
    // A PROVIDER account with no provider profile, as on `provider.getOwn`. Never configured is
    // `200` with `configured: false`, not `404` (§4).
    errors: ["NOT_FOUND"]
  }),
  defineRoute({
    id: "availability.replaceOwn",
    method: "PUT",
    path: "/api/providers/me/availability",
    summary: "Replace the signed-in provider's whole availability calendar.",
    tags: ["availability"],
    auth: "session",
    body: AvailabilityWriteSchema,
    responses: { 200: AvailabilityOwnSchema },
    // `VALIDATION_FAILED` comes with the body. Last write wins, so there is no `CONFLICT` (§3.7).
    errors: ["NOT_FOUND"]
  }),
  defineRoute({
    id: "availability.get",
    method: "GET",
    path: "/api/providers/{id}/availability",
    summary: "Read a provider's weekly hours and upcoming blocked dates.",
    tags: ["availability"],
    auth: "public",
    params: z.strictObject({ id: ProviderIdSchema }),
    responses: { 200: AvailabilitySchema },
    // A malformed provider id is `400`, as on `provider.get`; an unknown or suspended one is `404`.
    errors: ["NOT_FOUND", "VALIDATION_FAILED"]
  }),
  // ── Jobs ─────────────────────────────────────────────────────────────────────────────────
  defineRoute({
    id: "job.create",
    method: "POST",
    path: "/api/jobs",
    summary: "Create a job as a draft.",
    tags: ["jobs"],
    auth: "session",
    body: JobDraftInputSchema,
    responses: { 201: JobSchema },
    errors: []
  }),
  defineRoute({
    id: "job.get",
    method: "GET",
    path: "/api/jobs/{id}",
    summary: "Read one of your own jobs.",
    tags: ["jobs"],
    auth: "session",
    params: IdParams,
    responses: { 200: JobSchema },
    errors: ["NOT_FOUND"]
  }),
  defineRoute({
    id: "job.update",
    method: "PUT",
    path: "/api/jobs/{id}",
    summary: "Edit one of your own jobs while it is editable.",
    tags: ["jobs"],
    auth: "session",
    params: IdParams,
    body: JobUpdateInputSchema,
    responses: { 200: JobSchema },
    errors: ["NOT_FOUND", "CONFLICT"]
  }),
  defineRoute({
    id: "job.publish",
    method: "POST",
    path: "/api/jobs/{id}/publish",
    summary: "Publish one of your own draft jobs.",
    tags: ["jobs"],
    auth: "session",
    params: IdParams,
    responses: { 200: JobSchema },
    // `ASK_PAYMENT_REQUIRED`: while charging is on, publishing goes through `job.publishCheckout`
    // (`W5-T12` §4.5) — answered after the ownership and draft checks, before any write.
    errors: ["NOT_FOUND", "CONFLICT", "ASK_PAYMENT_REQUIRED"]
  }),
  defineRoute({
    id: "job.cancel",
    method: "POST",
    path: "/api/jobs/{id}/cancel",
    summary: "Cancel one of your own jobs, optionally saying why.",
    tags: ["jobs"],
    auth: "session",
    params: IdParams,
    body: JobCancelInputSchema,
    responses: { 200: JobSchema },
    errors: ["NOT_FOUND", "CONFLICT"]
  }),
  defineRoute({
    id: "job.feed",
    method: "GET",
    path: "/api/me/job-feed",
    summary: "The open jobs a provider can quote on, paged by cursor.",
    tags: ["jobs"],
    auth: "session",
    query: JobFeedQuerySchema,
    responses: { 200: JobFeedPageSchema },
    errors: ["CONFLICT"]
  }),
  defineRoute({
    id: "client.getAddress",
    method: "GET",
    path: "/api/clients/me/address",
    summary: "The signed-in client's remembered address, or null.",
    tags: ["jobs"],
    auth: "session",
    responses: { 200: SavedAddressSchema },
    errors: []
  }),
  // ── Client profile and address book (`W2-T04`) ─────────────────────────────────────────────
  defineRoute({
    id: "client.getProfile",
    method: "GET",
    path: "/api/clients/me",
    summary: "The signed-in client's display name and default address.",
    tags: ["clients"],
    auth: "session",
    responses: { 200: ClientProfileSchema },
    errors: []
  }),
  defineRoute({
    id: "client.updateProfile",
    method: "PUT",
    path: "/api/clients/me",
    summary: "Change the signed-in client's display name.",
    tags: ["clients"],
    auth: "session",
    body: ClientProfileUpdateSchema,
    responses: { 200: ClientProfileSchema },
    errors: []
  }),
  defineRoute({
    id: "client.listAddresses",
    method: "GET",
    path: "/api/clients/me/addresses",
    summary: "The signed-in client's address book, default first.",
    tags: ["clients"],
    auth: "session",
    responses: { 200: SavedAddressListSchema },
    errors: []
  }),
  defineRoute({
    id: "client.createAddress",
    method: "POST",
    path: "/api/clients/me/addresses",
    summary: "Add an address to the signed-in client's book; 409 when the book is full.",
    tags: ["clients"],
    auth: "session",
    body: SavedAddressCreateSchema,
    responses: { 201: SavedAddressEntrySchema },
    // The cap is the only conflict this route has. `CONFLICT` rather than a code of its own:
    // `W1-T01` AC3 gives each status exactly one code, and 409 is taken.
    errors: ["CONFLICT"]
  }),
  defineRoute({
    id: "client.updateAddress",
    method: "PUT",
    path: "/api/clients/me/addresses/{addressId}",
    summary: "Replace one entry of your address book; the entry gets a new id.",
    tags: ["clients"],
    auth: "session",
    params: AddressParams,
    body: SavedAddressCreateSchema,
    responses: { 200: SavedAddressEntrySchema },
    errors: ["NOT_FOUND"]
  }),
  defineRoute({
    id: "client.deleteAddress",
    method: "DELETE",
    path: "/api/clients/me/addresses/{addressId}",
    summary: "Remove one entry from your address book.",
    tags: ["clients"],
    auth: "session",
    params: AddressParams,
    responses: { 204: null },
    errors: ["NOT_FOUND"]
  }),
  defineRoute({
    id: "client.setDefaultAddress",
    method: "PUT",
    path: "/api/clients/me/default-address",
    summary: "Make one entry of your address book the default.",
    tags: ["clients"],
    auth: "session",
    body: DefaultAddressInputSchema,
    responses: { 200: ClientProfileSchema },
    errors: ["NOT_FOUND"]
  }),
  // ── Auctions and bids ────────────────────────────────────────────────────────────────────
  defineRoute({
    id: "auction.publish",
    method: "POST",
    path: "/api/jobs/{id}/auction",
    summary: "Publish one of your own jobs as an auction.",
    tags: ["auctions"],
    auth: "session",
    params: IdParams,
    body: AuctionPublishInputSchema,
    responses: { 201: AuctionPublishedSchema },
    // `ASK_PAYMENT_REQUIRED`: as `job.publish` — while charging is on, an auction opens through
    // `job.publishCheckout` (`W5-T12` §4.5).
    errors: ["NOT_FOUND", "CONFLICT", "ASK_PAYMENT_REQUIRED"]
  }),
  defineRoute({
    id: "auction.get",
    method: "GET",
    path: "/api/jobs/{id}/auction",
    summary: "Read the auction on one of your own jobs.",
    tags: ["auctions"],
    auth: "session",
    params: IdParams,
    responses: { 200: AuctionSchema },
    errors: ["NOT_FOUND"]
  }),
  defineRoute({
    id: "auction.listBids",
    method: "GET",
    path: "/api/jobs/{id}/auction/bids",
    summary: "The bids on the auction on one of your own jobs.",
    tags: ["auctions"],
    auth: "session",
    params: IdParams,
    responses: { 200: AuctionBidListSchema },
    errors: ["NOT_FOUND"]
  }),
  defineRoute({
    id: "auction.award",
    method: "POST",
    path: "/api/jobs/{id}/auction/award",
    summary: "Choose a bid on the auction on one of your own jobs.",
    tags: ["auctions"],
    auth: "session",
    params: IdParams,
    body: AuctionAwardInputSchema,
    responses: { 200: AuctionSchema },
    errors: ["NOT_FOUND", "CONFLICT"]
  }),
  defineRoute({
    id: "bid.create",
    method: "POST",
    path: "/api/jobs/{id}/bids",
    summary: "Place a bid on an auctioned job.",
    tags: ["auctions"],
    auth: "session",
    params: IdParams,
    body: BidInputSchema,
    responses: { 201: BidSchema },
    // `PAYOUTS_NOT_READY`: the payout gate (`W5-T01` §4.7) refuses a provider who is not `READY`.
    // `SUBSCRIPTION_REQUIRED`: the same gate refuses, first, a provider not entitled (`W5-T13` §4.1).
    errors: ["NOT_FOUND", "CONFLICT", "PAYOUTS_NOT_READY", "SUBSCRIPTION_REQUIRED"]
  }),
  defineRoute({
    id: "bid.withdraw",
    method: "POST",
    path: "/api/bids/{id}/withdraw",
    summary: "Withdraw one of your own bids.",
    tags: ["auctions"],
    auth: "session",
    params: IdParams,
    responses: { 200: BidSchema },
    errors: ["NOT_FOUND", "CONFLICT"]
  }),
  defineRoute({
    id: "bid.listOwn",
    method: "GET",
    path: "/api/me/bids",
    summary: "The signed-in provider's own bids.",
    tags: ["auctions"],
    auth: "session",
    responses: { 200: BidListSchema },
    errors: []
  }),
  defineRoute({
    id: "bid.listForJob",
    method: "GET",
    path: "/api/jobs/{id}/bids/mine",
    summary: "The signed-in provider's own bids on one job's auction.",
    tags: ["auctions"],
    auth: "session",
    params: IdParams,
    responses: { 200: BidListSchema },
    errors: ["NOT_FOUND"]
  }),
  // ── Job threads ──────────────────────────────────────────────────────────────────────────
  defineRoute({
    id: "thread.getOwn",
    method: "GET",
    path: "/api/jobs/{id}/thread",
    summary: "A provider's own thread with the client on a job.",
    tags: ["threads"],
    auth: "session",
    params: IdParams,
    responses: { 200: JobThreadViewSchema },
    errors: ["NOT_FOUND"]
  }),
  defineRoute({
    id: "thread.sendOwn",
    method: "POST",
    path: "/api/jobs/{id}/thread/messages",
    summary: "A provider writes to the client on a job.",
    tags: ["threads"],
    auth: "session",
    params: IdParams,
    body: JobMessageInputSchema,
    responses: { 201: JobMessageSchema },
    errors: ["NOT_FOUND", "CONFLICT"]
  }),
  defineRoute({
    id: "thread.list",
    method: "GET",
    path: "/api/jobs/{id}/threads",
    summary: "The threads on one of your own jobs.",
    tags: ["threads"],
    auth: "session",
    params: IdParams,
    responses: { 200: JobThreadListSchema },
    errors: ["NOT_FOUND"]
  }),
  defineRoute({
    id: "thread.get",
    method: "GET",
    path: "/api/jobs/{id}/threads/{threadId}",
    summary: "One thread on one of your own jobs.",
    tags: ["threads"],
    auth: "session",
    params: ThreadParams,
    responses: { 200: JobThreadViewSchema },
    errors: ["NOT_FOUND"]
  }),
  defineRoute({
    id: "thread.send",
    method: "POST",
    path: "/api/jobs/{id}/threads/{threadId}/messages",
    summary: "A client writes to a provider on one of their own jobs.",
    tags: ["threads"],
    auth: "session",
    params: ThreadParams,
    body: JobMessageInputSchema,
    responses: { 201: JobMessageSchema },
    errors: ["NOT_FOUND", "CONFLICT"]
  }),
  // ── Quotes ───────────────────────────────────────────────────────────────────────────────
  defineRoute({
    id: "quote.create",
    method: "POST",
    path: "/api/jobs/{id}/quotes",
    summary: "Quote on an open job.",
    tags: ["quotes"],
    auth: "session",
    params: IdParams,
    body: QuoteInputSchema,
    responses: { 201: QuoteSchema },
    // `PAYOUTS_NOT_READY`: the payout gate (`W5-T01` §4.7) refuses a provider who is not `READY`.
    // `SUBSCRIPTION_REQUIRED`: the same gate refuses, first, a provider not entitled (`W5-T13` §4.1).
    // `QUOTE_REQUEST_FULL`: the job has quotes from as many providers as its windows allow and the
    // caller holds no slot (`W5-T08` §4.1) — checked inside the insert transaction, under the job row
    // lock, after the gate's reasons and after `canQuoteOn`. `bid.create` has no such cap (§4.2).
    // `QUOTE_REQUEST_CLOSED`: every window of the job was refunded empty and the caller holds no slot
    // (`W5-T12` §3.3, E8, E12 = A) — the same check, in the same place, as `QUOTE_REQUEST_FULL`.
    errors: [
      "NOT_FOUND",
      "CONFLICT",
      "PAYOUTS_NOT_READY",
      "SUBSCRIPTION_REQUIRED",
      "QUOTE_REQUEST_FULL",
      "QUOTE_REQUEST_CLOSED"
    ]
  }),
  defineRoute({
    id: "quote.listForJob",
    method: "GET",
    path: "/api/jobs/{id}/quotes",
    summary: "The quotes on one of your own jobs, paged by cursor.",
    tags: ["quotes"],
    auth: "session",
    params: IdParams,
    query: QuoteListQuerySchema,
    responses: { 200: QuotePageSchema },
    errors: ["NOT_FOUND"]
  }),
  defineRoute({
    id: "quote.withdraw",
    method: "POST",
    path: "/api/quotes/{id}/withdraw",
    summary: "Withdraw one of your own quotes.",
    tags: ["quotes"],
    auth: "session",
    params: IdParams,
    responses: { 200: QuoteSchema },
    errors: ["NOT_FOUND", "CONFLICT"]
  }),
  defineRoute({
    id: "quote.accept",
    method: "POST",
    path: "/api/quotes/{id}/accept",
    summary: "Accept a quote on one of your own jobs.",
    tags: ["quotes"],
    auth: "session",
    params: IdParams,
    responses: { 200: QuoteSchema },
    errors: ["NOT_FOUND", "CONFLICT"]
  }),
  defineRoute({
    id: "quote.reject",
    method: "POST",
    path: "/api/quotes/{id}/reject",
    summary: "Reject a quote on one of your own jobs.",
    tags: ["quotes"],
    auth: "session",
    params: IdParams,
    responses: { 200: QuoteSchema },
    errors: ["NOT_FOUND", "CONFLICT"]
  }),
  // `W4-T17` §4. A malformed id is `404 NOT_FOUND`, as accept (`ADR-016`), so `VALIDATION_FAILED` is
  // not listed: the generator adds it for the body, whose any key is a `400` (AC32). `CONFLICT` is
  // a job not `OPEN` or a quote with no `UNACCEPT` row; `QUOTE_PAYMENT_PENDING` (E3 = B) is the
  // guard's refusal, raised by name by the repository and by this route only (AC40).
  defineRoute({
    id: "quote.unaccept",
    method: "POST",
    path: "/api/quotes/{id}/unaccept",
    summary: "Undo the acceptance of a quote on one of your own jobs.",
    tags: ["quotes"],
    auth: "session",
    params: IdParams,
    body: QuoteUnacceptInputSchema,
    responses: { 200: QuoteSchema },
    errors: ["NOT_FOUND", "CONFLICT", "QUOTE_PAYMENT_PENDING"]
  }),
  // ── Bookings (`W5-T02`) ──────────────────────────────────────────────────────────────────
  //
  // Content frozen in `W1-T11` §4.2 (decision E1). A malformed path id answers the route's own
  // not-found code, never `400` (`ADR-016`), and absent or not-yours answer the same (W5-T02 §4.1),
  // so no `NOT_FOUND` is listed. `201` creates the booking; `200` answers the caller's live one on
  // the same quote (W5-T02 §4.1).
  defineRoute({
    id: "booking.create",
    method: "POST",
    path: "/api/quotes/{id}/booking",
    summary: "Book an accepted quote and start paying its call-out fee.",
    tags: ["bookings"],
    auth: "session",
    params: IdParams,
    body: CreateBookingInputSchema,
    responses: { 200: BookingPaymentSchema, 201: BookingPaymentSchema },
    errors: [
      "QUOTE_NOT_FOUND",
      "QUOTE_NOT_PAYABLE",
      "JOB_NOT_AWARDABLE",
      "JOB_ALREADY_AWARDED",
      "PAYMENTS_UNAVAILABLE"
    ]
  }),
  defineRoute({
    id: "booking.get",
    method: "GET",
    path: "/api/bookings/{id}",
    summary: "Read a booking you are a party to.",
    tags: ["bookings"],
    auth: "session",
    params: IdParams,
    responses: { 200: BookingSchema },
    errors: ["BOOKING_NOT_FOUND"]
  }),
  // `W5-T18` §4.7. By job, not by booking: the professional's screen has the job id only. One answer
  // for no such job, no booking and not its provider (the job's client included), as `booking.get`;
  // and a malformed id answers `BOOKING_NOT_FOUND` too, the route's own 404 (`ADR-016`), so neither
  // `NOT_FOUND` nor `VALIDATION_FAILED` is listed. A `GET`, so no `ROUTE_LIMITS` row.
  defineRoute({
    id: "release.get",
    method: "GET",
    path: "/api/jobs/{id}/release",
    summary: "Where the professional's share of a released call-out fee stands.",
    tags: ["bookings"],
    auth: "session",
    params: IdParams,
    responses: { 200: CalloutReleaseViewSchema },
    errors: ["BOOKING_NOT_FOUND"]
  }),
  // ── Documents (`W5-T09`) ─────────────────────────────────────────────────────────────────
  //
  // Spec §4.4, §4.5, §6. A document is its recipient's alone: the booking's other party, an unrelated
  // user and an admin who is not the recipient all get `404 DOCUMENT_NOT_FOUND`, and so does a
  // malformed id, the route's own 404 (`ADR-016`) — so neither `NOT_FOUND` nor `VALIDATION_FAILED`
  // is listed. The generator adds `VALIDATION_FAILED` to the two that parse a query (a bad `limit`,
  // `cursor`, `bookingId` or `lang`, AC 28). All `GET`, so no `ROUTE_LIMITS` row. `document.pdf`
  // answers bytes (E6 = A): its `200` is `PDF_BODY`, not a zod schema.
  defineRoute({
    id: "document.list",
    method: "GET",
    path: "/api/me/documents",
    summary: "List the signed-in user's receipts and payout statements, newest first.",
    tags: ["documents"],
    auth: "session",
    query: MoneyDocumentListQuerySchema,
    responses: { 200: MoneyDocumentPageSchema },
    errors: []
  }),
  defineRoute({
    id: "document.get",
    method: "GET",
    path: "/api/documents/{id}",
    summary: "Read one of your provisional money documents.",
    tags: ["documents"],
    auth: "session",
    params: IdParams,
    responses: { 200: MoneyDocumentSchema },
    errors: ["DOCUMENT_NOT_FOUND"]
  }),
  defineRoute({
    id: "document.pdf",
    method: "GET",
    path: "/api/documents/{id}/pdf",
    summary: "Download one of your provisional money documents as a PDF.",
    tags: ["documents"],
    auth: "session",
    params: IdParams,
    query: MoneyDocumentPdfQuerySchema,
    responses: { 200: PDF_BODY },
    errors: ["DOCUMENT_NOT_FOUND"]
  }),
  // ── Payouts (`W5-T01`) ───────────────────────────────────────────────────────────────────
  //
  // Spec §4.1, §4.2. The caller's own account, so neither path has a parameter, and there is no
  // 404-or-400 decision to make (`ADR-016`). `CONFLICT` is a provider role with no profile, the same
  // answer `quote.create` gives. `PAYMENTS_UNAVAILABLE` is unset config or Stripe unreachable, and
  // only the link route calls Stripe: the status read never does.
  defineRoute({
    id: "payouts.status",
    method: "GET",
    path: "/api/me/payouts",
    summary: "The signed-in provider's payout status, and whether they can quote and bid.",
    tags: ["payouts"],
    auth: "session",
    responses: { 200: PayoutStatusSchema },
    errors: ["CONFLICT"]
  }),
  defineRoute({
    id: "payouts.onboardingLink",
    method: "POST",
    path: "/api/me/payouts/onboarding-link",
    summary: "Start or resume Stripe-hosted payout onboarding; answers a single-use link.",
    tags: ["payouts"],
    auth: "session",
    body: PayoutOnboardingLinkInputSchema,
    responses: { 200: PayoutOnboardingLinkSchema },
    errors: ["CONFLICT", "PAYOUTS_ONBOARDING_CLOSED", "PAYMENTS_UNAVAILABLE"]
  }),
  // ── Subscriptions (`W5-T07`) ─────────────────────────────────────────────────────────────
  //
  // Spec §4.1–§4.5. The caller's own subscription, so no path has a parameter, and there is no
  // 404-or-400 decision to make (`ADR-016`). `CONFLICT` is a provider role with no profile, the same
  // answer `payouts.status` gives. `PAYMENTS_UNAVAILABLE` is unset config, Stripe unreachable, a price
  // that fails the lookup's checks, or a hosted URL on the wrong host; the subscription read never
  // calls Stripe, so it never answers it. The portal takes no body: who it is for is the session.
  defineRoute({
    id: "subscription.get",
    method: "GET",
    path: "/api/me/subscription",
    summary: "The signed-in provider's subscription: status, plan, period end; nothing of Stripe's.",
    tags: ["subscriptions"],
    auth: "session",
    responses: { 200: ProviderSubscriptionSchema },
    errors: ["CONFLICT"]
  }),
  defineRoute({
    id: "subscription.plans",
    method: "GET",
    path: "/api/subscription/plans",
    summary: "What a provider subscription costs, for each kind; any signed-in user may read it.",
    tags: ["subscriptions"],
    auth: "session",
    responses: { 200: SubscriptionPlansSchema },
    errors: ["PAYMENTS_UNAVAILABLE"]
  }),
  defineRoute({
    id: "subscription.checkout",
    method: "POST",
    path: "/api/me/subscription/checkout",
    summary: "Start subscribing at the profile's kind; answers a Stripe Checkout URL.",
    tags: ["subscriptions"],
    auth: "session",
    body: SubscriptionCheckoutInputSchema,
    responses: { 200: HostedRedirectSchema },
    errors: ["CONFLICT", "SUBSCRIPTION_EXISTS", "PAYMENTS_UNAVAILABLE"]
  }),
  defineRoute({
    id: "subscription.changePlan",
    method: "POST",
    path: "/api/me/subscription/plan",
    summary: "Switch kind, and the subscription price with it, prorated.",
    tags: ["subscriptions"],
    auth: "session",
    body: SubscriptionPlanInputSchema,
    responses: { 200: ProviderSubscriptionSchema },
    errors: [
      "CONFLICT",
      "SUBSCRIPTION_NOT_CHANGEABLE",
      "SUBSCRIPTION_PAYMENT_FAILED",
      "PAYMENTS_UNAVAILABLE"
    ]
  }),
  defineRoute({
    id: "subscription.portal",
    method: "POST",
    path: "/api/me/subscription/portal",
    summary: "Open Stripe's customer portal: card, invoices, cancellation; answers its URL.",
    tags: ["subscriptions"],
    auth: "session",
    responses: { 200: HostedRedirectSchema },
    errors: ["CONFLICT", "SUBSCRIPTION_NOT_FOUND", "PAYMENTS_UNAVAILABLE"]
  }),
  // ── Charge to ask (`W5-T12`) ─────────────────────────────────────────────────────────────
  //
  // Spec §4.1–§4.4, §4.9. The three job routes take the job's id through `IdParams`: not the caller's,
  // no such job, or a malformed id is `404 NOT_FOUND`, like every job route (`ADR-016`).
  // `PAYMENTS_UNAVAILABLE` is a price, product or tax rate that fails its check, Stripe unreachable,
  // or a hosted URL on the wrong host; the owner's read never calls Stripe, so it never answers it.
  // The quote-window checkout takes **no body** (§4.4), and answers a body with keys `400`, so it
  // lists `VALIDATION_FAILED` itself — the generator adds `400` only for a query or a body.
  defineRoute({
    id: "ask.prices",
    method: "GET",
    path: "/api/ask/prices",
    summary: "What asking costs: a window of quotes and an auction, in cents, with their tax.",
    tags: ["ask"],
    auth: "session",
    responses: { 200: AskPricesSchema },
    errors: ["PAYMENTS_UNAVAILABLE"]
  }),
  defineRoute({
    id: "job.ask",
    method: "GET",
    path: "/api/jobs/{id}/ask",
    summary: "What you paid to ask on one of your own jobs: its windows, refunds and auction fee.",
    tags: ["ask"],
    auth: "session",
    params: IdParams,
    responses: { 200: JobAskSchema },
    errors: ["NOT_FOUND"]
  }),
  defineRoute({
    id: "job.publishCheckout",
    method: "POST",
    path: "/api/jobs/{id}/publish/checkout",
    summary: "Pay to publish one of your own draft jobs, for quotes or as an auction; answers a Stripe Checkout URL.",
    tags: ["ask"],
    auth: "session",
    params: IdParams,
    body: PublishCheckoutInputSchema,
    responses: { 200: HostedRedirectSchema },
    errors: ["NOT_FOUND", "CONFLICT", "ASK_PAYMENT_CONFIRMING", "PAYMENTS_UNAVAILABLE"]
  }),
  defineRoute({
    id: "quoteWindow.checkout",
    method: "POST",
    path: "/api/jobs/{id}/quote-windows/checkout",
    summary: "Pay for another window of quotes on one of your own open jobs; answers a Stripe Checkout URL.",
    tags: ["ask"],
    auth: "session",
    params: IdParams,
    responses: { 200: HostedRedirectSchema },
    errors: [
      "NOT_FOUND",
      "VALIDATION_FAILED",
      "CONFLICT",
      "QUOTE_WINDOW_HAS_ROOM",
      "ASK_PAYMENT_CONFIRMING",
      "PAYMENTS_UNAVAILABLE"
    ]
  }),
  // ── Handshake (`W4-T09`) ─────────────────────────────────────────────────────────────────
  //
  // Spec §4. Issue and read take the job id in the path, parsed with `IdParams`: a malformed id is
  // `404 NOT_FOUND` like every job route (`ADR-016`), so `VALIDATION_FAILED` is not listed for them.
  // No such job, or a caller who is not a party, answers the same `NOT_FOUND`, so existence never
  // leaks. Redeem has no path id — a token names its own booking — and its body is the union of §4.3,
  // whose `400` the generator adds. `PAYMENTS_UNAVAILABLE` is money's port unwired or failing (§6).
  defineRoute({
    id: "handshake.issue",
    method: "POST",
    path: "/api/jobs/{id}/handshake",
    summary: "Issue the start-of-work code for a job you were awarded; supersedes any live one.",
    tags: ["handshake"],
    auth: "session",
    params: IdParams,
    responses: { 201: HandshakeIssuedSchema },
    errors: ["NOT_FOUND", "HANDSHAKE_NOT_AVAILABLE", "HANDSHAKE_ALREADY_REDEEMED"]
  }),
  defineRoute({
    id: "handshake.get",
    method: "GET",
    path: "/api/jobs/{id}/handshake",
    summary: "Read where a job's start-of-work code stands; never the code itself.",
    tags: ["handshake"],
    auth: "session",
    params: IdParams,
    responses: { 200: HandshakeStateSchema },
    errors: ["NOT_FOUND"]
  }),
  defineRoute({
    id: "handshake.redeem",
    method: "POST",
    path: "/api/handshake/redeem",
    summary: "Confirm, as the booking's client, that work has started: redeem the professional's code.",
    tags: ["handshake"],
    auth: "session",
    body: HandshakeRedeemInputSchema,
    responses: { 200: HandshakeRedeemedSchema },
    errors: [
      "HANDSHAKE_NOT_FOUND",
      "HANDSHAKE_WRONG_PARTY",
      "HANDSHAKE_EXPIRED",
      "HANDSHAKE_ALREADY_REDEEMED",
      "HANDSHAKE_CODE_INVALID",
      "HANDSHAKE_NOT_AVAILABLE",
      "PAYMENTS_UNAVAILABLE"
    ]
  }),
  // ── Webhooks (`W5-T03`) ──────────────────────────────────────────────────────────────────
  //
  // One path per Stripe scope, each with its own signing secret (spec §4.1). `public`: authenticated
  // by the `Stripe-Signature` header, **never** by a session — a signed-in user without a signature
  // is `400` like anyone else. `body` documents the payload and is **not** what the handler parses
  // with: the raw bytes are verified first and the Stripe SDK parses them, so the identity rule in
  // this file's header cannot hold here (`StripeEventEnvelopeSchema`). No `params`, so no
  // 404-or-400 decision. `RATE_LIMITED` and `INTERNAL_ERROR` (insert failed, nothing stored, Stripe
  // retries) come from the generator.
  defineRoute({
    id: "webhook.stripe",
    method: "POST",
    path: "/api/webhooks/stripe",
    summary: "Stripe delivers a platform-scope event; verified by its signature, stored once.",
    tags: ["webhooks"],
    auth: "public",
    body: StripeEventEnvelopeSchema,
    responses: { 200: StripeWebhookAckSchema },
    errors: ["WEBHOOK_SIGNATURE_INVALID", "PAYLOAD_TOO_LARGE", "UNSUPPORTED_MEDIA_TYPE"]
  }),
  defineRoute({
    id: "webhook.stripeConnect",
    method: "POST",
    path: "/api/webhooks/stripe/connect",
    summary: "Stripe delivers a connected-account event; verified by its own signature, stored once.",
    tags: ["webhooks"],
    auth: "public",
    body: StripeEventEnvelopeSchema,
    responses: { 200: StripeWebhookAckSchema },
    errors: ["WEBHOOK_SIGNATURE_INVALID", "PAYLOAD_TOO_LARGE", "UNSUPPORTED_MEDIA_TYPE"]
  })
];
validateRoutes(ROUTES);
function createClient({ baseUrl, fetch }) {
  return createFetchClient({ baseUrl, fetch });
}

export { AVAILABILITY_DEFAULT_TIMEZONE, AVAILABILITY_HORIZON_DAYS, AVAILABILITY_MAX_BLOCKED_DATES, AVAILABILITY_MAX_WINDOWS, AVAILABILITY_MAX_WINDOWS_PER_WEEKDAY, AVAILABILITY_MINUTES_PER_DAY, AVAILABILITY_MINUTE_GRAIN, AppError, AskPaymentKindSchema, AskPaymentStatusCodeSchema, AskPricesSchema, AuctionAwardInputSchema, AuctionBidListSchema, AuctionBidSchema, AuctionEventSchema, AuctionPublishInputSchema, AuctionPublishedSchema, AuctionSchema, AuctionStatusSchema, AuditRecordSchema, AvailabilityOwnSchema, AvailabilitySchema, AvailabilityWriteSchema, BID_MESSAGE_MAX, BidEventSchema, BidInputSchema, BidListSchema, BidResultSchema, BidSchema, BidStatusSchema, BlockedDateSchema, BookingPaymentSchema, BookingSchema, BookingStatusSchema, BudgetRangeSchema, CATEGORY_SLUG, CalloutReleaseStateSchema, CalloutReleaseViewSchema, CategoryListSchema, CategorySummarySchema, ClientProfileSchema, ClientProfileUpdateSchema, CreateBookingInputSchema, DOMAIN_ERROR_CODES, DefaultAddressInputSchema, ERROR_CODES, ErrorEnvelopeSchema, GENERIC_ERROR_CODES, HANDSHAKE_SHORT_CODE_PATTERN, HANDSHAKE_TOKEN_PATTERN, HandshakeIssuedSchema, HandshakeRedeemInputSchema, HandshakeRedeemedSchema, HandshakeStateSchema, HandshakeStatusSchema, HostedRedirectSchema, INTERNAL_ERROR_MESSAGE, JOB_FEED_DEFAULT_SORT, JOB_FEED_SORTABLE, JobAddressInputSchema, JobAskSchema, JobCancelInputSchema, JobCategorySchema, JobDraftInputSchema, JobEventSchema, JobFeedCoverageSchema, JobFeedItemSchema, JobFeedMyQuoteSchema, JobFeedPageSchema, JobFeedQuerySchema, JobLocationSchema, JobMessageInputSchema, JobMessageSchema, JobModeSchema, JobSchema, JobStatusSchema, JobThreadListSchema, JobThreadSchema, JobThreadSummarySchema, JobThreadViewSchema, JobUpdateInputSchema, JobUrgencySchema, LISTING_DESCRIPTION_MAX, LISTING_MAX_PER_PROVIDER, LISTING_TITLE_MAX, LISTING_TITLE_MIN, ListingIdSchema, ListingListSchema, ListingOwnListSchema, ListingOwnSchema, ListingPriceModelSchema, ListingSchema, ListingWriteSchema, MAX_SORT_FIELDS, MESSAGE_BODY_MAX, MONEY_DOCUMENT_DEFAULT_SORT, MONEY_DOCUMENT_LANGS, MONEY_DOCUMENT_PAGE_LIMIT_MAX, MONEY_DOCUMENT_SORTABLE, MessageSideSchema, MinuteOfDaySchema, MoneyDocumentIssuerSchema, MoneyDocumentKindSchema, MoneyDocumentListQuerySchema, MoneyDocumentPageSchema, MoneyDocumentPaymentSchema, MoneyDocumentPdfQuerySchema, MoneyDocumentRecipientSchema, MoneyDocumentSchema, MoneyDocumentSummarySchema, MoneyError, MoneySchema, PAGE_LIMIT_DEFAULT, PAGE_LIMIT_MAX, PDF_BODY, POINT_DECIMALS, PROVIDER_CATEGORY_MAX, PageInfoSchema, PaginationError, PaymentStatusSchema, PayoutOnboardingLinkInputSchema, PayoutOnboardingLinkSchema, PayoutStatementSchema, PayoutStatusCodeSchema, PayoutStatusSchema, PlatformFeeReceiptSchema, ProviderAddressCreateSchema, ProviderAddressUpdateSchema, ProviderAddressWriteSchema, ProviderIdSchema, ProviderKindSchema, ProviderProfileCreateSchema, ProviderProfileOwnSchema, ProviderProfileSchema, ProviderProfileUpdateSchema, ProviderProfileWriteSchema, ProviderSubscriptionSchema, PublishCheckoutInputSchema, QUOTE_DEFAULT_SORT, QUOTE_MIN_AMOUNT_CENTS, QUOTE_SORTABLE, QuoteCoverageSchema, QuoteEventSchema, QuoteInputSchema, QuoteListQuerySchema, QuotePageSchema, QuoteProviderSchema, QuoteSchema, QuoteStateSchema, QuoteStatusSchema, QuoteUnacceptInputSchema, QuoteWindowStatusSchema, QuoteWindowViewSchema, RESERVED_QUERY_KEYS, ROUTES, ReceiptTypeSchema, RegistryError, RevealPolicySchema, SEARCH_DEFAULT_SORT, SEARCH_SORTABLE, SERVICE_RADIUS_MAX_METRES, SavedAddressCreateSchema, SavedAddressEntrySchema, SavedAddressListSchema, SavedAddressSchema, SearchCategorySchema, SearchFacetsSchema, SearchModeSchema, SearchPointSchema, SearchQuerySchema, SearchResponseSchema, SearchResultSchema, SearchUrgencySchema, StateMachineDefinitionError, StripeEventEnvelopeSchema, StripeWebhookAckSchema, SubscriptionCheckoutInputSchema, SubscriptionPlanInputSchema, SubscriptionPlansSchema, SubscriptionStatusCodeSchema, THREAD_LIST_LIMIT, THREAD_VIEW_LIMIT, TIEBREAKER, TransitionRejected, WeekdaySchema, WeeklyWindowSchema, add, allocate, applyBasisPoints, auctionMachine, bidMachine, bidResultOf, binaryBody, can, canDecideOn, canEditJob, canOpenThreadOn, canQuoteInMode, canQuoteOn, canWriteThreadOn, check, coarsenPoint, codeForStatus, compare, createClient, decodeCursor, defineMachine, defineRoute, emptyPage, encodeCursor, equals, errorEnvelope, fetchLimit, hasAnyCategory, isBinaryBody, isNegative, isPositive, isZero, ivaOf, jobMachine, keysetPredicate, listQuery, max, min, money, multiply, negate, pageEnvelope, pageOf, paginationQuery, parseSort, prorate, quoteMachine, quoteStatusOf, split, subtract, sum, transition, validateRoutes, withIva, zero };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map